import { useEffect, useMemo, useRef, useState } from 'react';
import { offboardContact, deleteFirm, updateContact, updateFirm, syncFirmScores, syncContactsSheetToBq, syncContactsBqToSheet, syncFirmsSheetToBq, syncFirmsBqToSheet, fetchContactViews, createContactView, updateContactView, deleteContactView, backfillFirmDefaults, createTrial } from '../api/backend.js';
import { fetchContactsPage, fetchFirmsPage, fetchFirmContacts, fetchContactEmailMap } from '../api/backend.js';
import { invalidateSheetCache } from '../hooks/useSheetData.js';
import useDebouncedValue from '../hooks/useDebouncedValue.js';
import { STAGE_BADGE_CLASS, STAGE_LABELS, PICKABLE_STAGES } from '../utils/stage.js';
import { csvEscape, downloadCsv } from '../utils/csv.js';
import StagePicker from '../components/StagePicker.jsx';
import MoveFirmModal from '../components/MoveFirmModal.jsx';
import BreakdownGrid from '../components/BreakdownGrid.jsx';
import BatchSplitPanel from '../components/BatchSplitPanel.jsx';
import AmplitudeScoreChip from '../components/AmplitudeScoreChip.jsx';
import SyncPanel from '../components/SyncPanel.jsx';
import DeduplicatePanel from '../components/DeduplicatePanel.jsx';
import EmailSuggestModal from '../components/EmailSuggestModal.jsx';
import { buildFirmPatterns, suggestEmailAvoidingTaken } from '../utils/emailPattern.js';

function useColumnResize(storageKey) {
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null') || {}; } catch { return {}; }
  });
  function save(widths) {
    setColWidths(widths);
    try { localStorage.setItem(storageKey, JSON.stringify(widths)); } catch {}
  }
  return [colWidths, save];
}

function ResizeHandle({ col, onResize }) {
  const startRef = useRef(null);

  function onMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    startRef.current = { x: e.clientX, w: th.offsetWidth };

    function onMove(ev) {
      const delta = ev.clientX - startRef.current.x;
      const newW = Math.max(40, startRef.current.w + delta);
      th.style.width = `${newW}px`;
      th.style.minWidth = `${newW}px`;
    }
    function onUp(ev) {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const delta = ev.clientX - startRef.current.x;
      const newW = Math.max(40, startRef.current.w + delta);
      onResize(col, newW);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  return (
    <span
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
        cursor: 'col-resize', userSelect: 'none', zIndex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: 2, height: '60%', borderRadius: 1,
        background: 'var(--c-border)',
        opacity: 0.6,
        transition: 'opacity 0.15s',
      }} />
    </span>
  );
}

const PAGE_SIZE = 200;

const DEAL_TIER_BADGE = {
  Hot:     { bg: '#fee2e2', fg: '#b91c1c' },
  Warm:    { bg: '#ffedd5', fg: '#c2410c' },
  Nurture: { bg: '#fef9c3', fg: '#a16207' },
  Cold:    { bg: '#e0f2fe', fg: '#0369a1' },
  Dead:    { bg: '#f1f5f9', fg: '#64748b' },
};

// Render order. The first 17 are Lilly's standard working set (also the CSV
// export order); the rest are opt-in via the Columns picker.
const CONTACT_COLUMNS = [
  'name','email','firm_name','sector_coverage','industry_focus','main_coverage','focus_region',
  'institution_type','focus_strategy','_firm_aum','_last_touch_ts','_touches','_last_email_open_ts',
  '_recent_touch_count','_stage','_bd_high_intent','bd_status','fund_type',
  'sector_packs','implied_sectors',
  'deal_tier','relevance_score','job_function','role','side','asset_class','added_date',
  'city','state_province','country_territory',
  'dominant_orientation','dominant_style','focus_orientation','focus_orientation_secondary',
  'focus_industry_macro','focus_industry_mid','focus_industry_micro','focus_market_cap','focus_style',
  'bd_aum_personal','account_aum','source',
];

const FIRM_COLUMNS = [
  'bd_status','firm_name','fund_type','aum','score','hq_city','contact_count','main_coverage_count','_stage_counts',
];

const FILTERABLE_FIRM_COLUMNS = FIRM_COLUMNS.filter((c) => c !== '_stage_counts' && c !== 'bd_status');

// AUM columns are stored in $M; their range filters take a $M/$B unit toggle
// (default $B — how the team quotes them: "1.8B", "50B"). Module scope: used
// by both FilterSection (the sidebar UI) and the page's buildFilters.
const AUM_UNIT_COLS = new Set(['_firm_aum', 'bd_aum_personal', 'account_aum']);

// Timeframe options for the Touches filter/column (_recent_touch_count).
// value = window in days sent to the backend ('all' = all-time); '90' matches
// the server's default when no window marker is sent, so it's only emitted
// into the filters array when it differs (see buildFilters).
const TOUCH_WINDOW_OPTIONS = [
  { value: '30',  label: 'Last 1 month',   short: '1mo' },
  { value: '90',  label: 'Last 3 months',  short: '3mo' },
  { value: '180', label: 'Last 6 months',  short: '6mo' },
  { value: '365', label: 'Last 12 months', short: '12mo' },
  { value: 'all', label: 'All time',       short: 'all' },
];
const DEFAULT_TOUCH_WINDOW = '90';
function touchWindowShort(w) {
  const v = String(w || DEFAULT_TOUCH_WINDOW);
  return TOUCH_WINDOW_OPTIONS.find((o) => o.value === v)?.short ?? `${v}d`;
}

const FILTERABLE_CONTACT_COLUMNS = [
  'name','email','role','source','added_date','relevance_score','sector_coverage','industry_focus','main_coverage',
  'job_function','dominant_orientation','dominant_style','city','state_province','country_territory','institution_type',
  'side','asset_class','bd_status','bd_aum_personal','account_aum','_firm_name','_fund_type','_firm_aum','_firm_owner','_last_touch_ts','_last_email_open_ts',
  'tags','sector_packs','implied_sectors',
];

const CONTACT_LABELS = {
  name: 'Name',
  firm_name: 'Firm',
  bd_status: 'Status',
  deal_tier: 'Deal',
  fund_type: 'Fund Type',
  relevance_score: 'Relevance',
  role: 'Role',
  _stage: 'Stage',
  sector_coverage: 'Coverage',
  industry_focus: 'Industry Focus',
  // Derived from industry_focus via the bd_sector_map dictionary (Specialist
  // contacts only) — see backend/migrations/0006_bd-sector-map.sql.
  implied_sectors: 'Implied Sector',
  sector_packs: 'Sector Pack',
  job_function: 'Job Function',
  _last_touch_ts: 'Last Touch',
  _last_email_open_ts: 'Last Email Open',
  _recent_touch_count: 'Touches',
  _touches: 'Touches (all-time)',
  _bd_high_intent: 'High Intent',
  _firm_owner: 'Owner',
  institution_type: 'Institution Type',
  side: 'Side',
  asset_class: 'Asset Class',
  added_date: 'Added',
  city: 'City',
  state_province: 'State',
  country_territory: 'Country',
  main_coverage: 'Main Coverage',
  dominant_orientation: 'Dom. Orientations',
  dominant_style: 'Dom. Style',
  focus_orientation: 'Focus Orientation',
  focus_orientation_secondary: 'Focus Orientation (EQ)',
  focus_industry_macro: 'Focus Industry Macro',
  focus_industry_mid: 'Focus Industry Mid',
  focus_industry_micro: 'Focus Industry Micro',
  focus_market_cap: 'Focus Mkt Cap',
  focus_region: 'Focus Region',
  focus_strategy: 'Focus Strategy',
  focus_style: 'Focus Style',
  _firm_aum: 'Firm AUM',
  bd_aum_personal: 'Personal AUM',
  account_aum: 'Account AUM',
  email: 'Email',
  source: 'Source',
  tags: 'Tags',
};

// type: 'stage'|'status'|'tag'|'owner'|'text'|'range'|'date'
const HEADER_FILTER_COLS = {
  // special
  _stage:                      'stage',
  // BD engagement without a reply — fixed Yes/No options, no distinct fetch
  // (a distinct over the derived boolean would run the whole derived table).
  _bd_high_intent:             'text',
  bd_status:                   'status',
  deal_tier:                   'text',
  _firm_owner:                 'owner',
  // tag (comma-separated multi-value)
  sector_coverage:             'tag',
  industry_focus:              'tag',
  implied_sectors:             'tag',
  sector_packs:                'tag',
  job_function:                'tag',
  institution_type:            'tag',
  side:                        'tag',
  asset_class:                 'tag',
  dominant_orientation:        'tag',
  dominant_style:              'tag',
  focus_orientation:           'tag',
  focus_orientation_secondary: 'tag',
  focus_industry_macro:        'tag',
  focus_industry_mid:          'tag',
  focus_industry_micro:        'tag',
  focus_market_cap:            'tag',
  focus_region:                'tag',
  focus_strategy:              'tag',
  focus_style:                 'tag',
  tags:                        'tag',
  // text (fetch distinct, single-value)
  name:                        'text',
  email:                       'text',
  role:                        'text',
  source:                      'text',
  city:                        'text',
  state_province:              'text',
  country_territory:           'text',
  _fund_type:                  'text',
  // range (numeric min/max)
  relevance_score:             'range',
  main_coverage:               'text',
  _firm_aum:                   'range',
  bd_aum_personal:             'range',
  account_aum:                 'range',
  _recent_touch_count:         'range',
  _touches:                    'range',
  // date (after/before)
  added_date:                  'date',
  _last_touch_ts:              'date',
  _last_email_open_ts:         'date',
};

const FIRM_HEADER_FILTER_COLS = {
  bd_status:     'status',
  firm_name:     'text',
  owner:         'text',
  fund_type:     'text',
  hq_city:       'text',
  aum:           'range',
  score:         'range',
  contact_count: 'range',
};

const CONTACT_FILTER_GROUPS = [
  { label: 'Stage & Status',    cols: ['_stage', '_bd_high_intent', 'bd_status'] },
  { label: 'Role & Coverage',   cols: ['job_function', 'main_coverage', 'role', '_firm_owner'] },
  { label: 'Sector & Industry', cols: ['sector_coverage', 'sector_packs', 'implied_sectors', 'industry_focus', 'institution_type', 'side', 'asset_class'] },
  { label: 'Style & Strategy',  cols: ['dominant_orientation', 'dominant_style', 'focus_orientation', 'focus_strategy', 'focus_market_cap', 'focus_region', 'focus_style'] },
  { label: 'Deep Focus',        cols: ['focus_orientation_secondary', 'focus_industry_macro', 'focus_industry_mid', 'focus_industry_micro'] },
  { label: 'Firm',              cols: ['_fund_type', 'source'] },
  { label: 'Location',          cols: ['city', 'state_province', 'country_territory'] },
  // _touches (all-time) is folded into the Touches filter's "All time"
  // timeframe — its column keeps a header filter, but no panel section.
  { label: 'Metrics',           cols: ['relevance_score', '_firm_aum', '_recent_touch_count', 'bd_aum_personal', 'account_aum'] },
  { label: 'Dates',             cols: ['added_date', '_last_touch_ts', '_last_email_open_ts'] },
];

const FIRM_FILTER_GROUPS = [
  { label: 'Status',  cols: ['bd_status'] },
  { label: 'Firm',    cols: ['firm_name', 'fund_type', 'owner', 'hq_city'] },
  { label: 'Metrics', cols: ['aum', 'score', 'contact_count'] },
];

const DEFAULT_CONTACT_VISIBLE = new Set([
  'name','email','firm_name','sector_coverage','industry_focus','main_coverage','focus_region',
  'institution_type','focus_strategy','_firm_aum','_last_touch_ts','_touches','_last_email_open_ts',
  '_recent_touch_count','_stage','_bd_high_intent','bd_status','fund_type',
]);

// Signal options offered inside the High Intent filter alongside Yes/No.
// Each checked signal becomes its own count filter (AND-ed), so
// "Yes + Clicked a link" = high intent AND url_link_views >= 1.
const BD_SIGNAL_OPTIONS = [
  { value: 'sig:opened',  label: 'Opened 2+ times' },
  { value: 'sig:clicked', label: 'Clicked a link' },
  { value: 'sig:file',    label: 'Viewed a file' },
];
const BD_SIGNAL_LABELS = Object.fromEntries(BD_SIGNAL_OPTIONS.map((o) => [o.value, o.label]));
const BD_SIGNAL_FILTERS = {
  'sig:opened':  { col: '_bd_read_count',      op: 'gte', val: '2' },
  'sig:clicked': { col: '_bd_url_link_views',  op: 'gte', val: '1' },
  'sig:file':    { col: '_bd_file_link_views', op: 'gte', val: '1' },
};

const INITIAL_COL_FILTERS = {
  _stage: [], _bd_high_intent: [], bd_status: [], sector_coverage: [], industry_focus: [], implied_sectors: [], sector_packs: [], job_function: [], _firm_owner: [],
  institution_type: [], side: [], asset_class: [], dominant_orientation: [], dominant_style: [],
  focus_orientation: [], focus_orientation_secondary: [], focus_industry_macro: [], focus_industry_mid: [],
  focus_industry_micro: [], focus_market_cap: [], focus_region: [], focus_strategy: [], focus_style: [],
  name: [], email: [], role: [], source: [], city: [], state_province: [], country_territory: [], _fund_type: [],
  relevance_score: { min: '', max: '', includeNull: false, onlyHasVal: false }, main_coverage: [],
  _firm_aum: { min: '', max: '', includeNull: false, onlyHasVal: false }, bd_aum_personal: { min: '', max: '', includeNull: false, onlyHasVal: false },
  account_aum: { min: '', max: '', includeNull: false, onlyHasVal: false }, _recent_touch_count: { min: '', max: '', includeNull: false, onlyHasVal: false },
  _touches: { min: '', max: '', includeNull: false, onlyHasVal: false },
  added_date: { after: '', before: '', nullMode: 'all' }, _last_touch_ts: { after: '', before: '', nullMode: 'all' }, _last_email_open_ts: { after: '', before: '', nullMode: 'all' },
};

const TAG_COLUMNS = new Set([
  'job_function',
  'industry_focus',
  'implied_sectors',
  'sector_packs',
  'sector_coverage',
  'industry_focus',
  'institution_type',
  'side',
  'asset_class',
  'dominant_orientation',
  'dominant_style',
  'focus_orientation',
  'focus_orientation_secondary',
  'focus_industry_macro',
  'focus_industry_mid',
  'focus_industry_micro',
  'focus_market_cap',
  'focus_region',
  'focus_strategy',
  'focus_style',
]);

function fmt(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (!isNaN(d)) return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
  return String(value);
}

function splitTags(value) {
  return String(value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function getInitials(name = '') {
  const parts = String(name).trim().split(/\s+/);
  if (!parts[0]) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function normalizeRows(rows = []) {
  // _stage and _last_touch_ts are now computed server-side — use them as-is
  return rows;
}

function getLastTouch(row) {
  const ts = row._last_touch_ts;
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  return d.toISOString();
}

function compactLabel(col) {
  return CONTACT_LABELS[col] ?? {
    firm_name:           'Firm',
    bd_status:           'Status',
    owner:               'Owner',
    fund_type:           'Fund Type',
    aum:                 'AUM',
    score:               'Score',
    hq_city:             'HQ City',
    contact_count:       '# Contacts',
    main_coverage_count: '# Contacts (Main Coverage)',
    _stage_counts:       'Stage Breakdown',
    _recent_touch_count: 'Touches',
  }[col] ?? col;
}

const BD_STATUS_VALUES = ['approved','compliance','uncontacted','rejected','blacklisted','contacted','unsubscribe','target'];

const BD_STATUS_STYLE = {
  approved:     { background: 'rgba(34,197,94,.15)',  color: '#16a34a', border: '1px solid rgba(34,197,94,.4)'  },
  compliance:   { background: 'rgba(234,179,8,.15)',  color: '#b45309', border: '1px solid rgba(234,179,8,.4)'  },
  uncontacted:  { background: 'rgba(148,163,184,.15)',color: '#64748b', border: '1px solid rgba(148,163,184,.4)'},
  rejected:     { background: 'rgba(239,68,68,.15)',  color: '#dc2626', border: '1px solid rgba(239,68,68,.4)'  },
  blacklisted:  { background: 'rgba(100,100,100,.15)',color: '#374151', border: '1px solid rgba(100,100,100,.4)'},
  contacted:    { background: 'rgba(99,102,241,.15)', color: '#4f46e5', border: '1px solid rgba(99,102,241,.4)' },
  unsubscribe:  { background: 'rgba(239,68,68,.1)',   color: '#9f1239', border: '1px solid rgba(239,68,68,.35)' },
  target:       { background: 'rgba(249,115,22,.15)', color: '#c2410c', border: '1px solid rgba(249,115,22,.4)' },
};

function StatusPicker({ value, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    function onDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function handleOpen() {
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left });
    setCustomText('');
    setOpen(true);
  }

  function handleCustomSubmit() {
    const trimmed = customText.trim();
    if (!trimmed) return;
    setOpen(false);
    onSave(trimmed);
  }

  const style = BD_STATUS_STYLE[value?.toLowerCase()] ?? {};

  return (
    <span ref={wrapRef} style={{ display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        disabled={saving}
        style={{
          ...style,
          borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer',
          whiteSpace: 'nowrap', display: 'inline-block',
          opacity: saving ? 0.6 : 1,
        }}
      >
        {value || '—'}
      </button>
      {open && (
        <div style={{
          position: 'fixed', top: pos.top, left: Math.min(pos.left, window.innerWidth - 180),
          zIndex: 9999, background: 'var(--c-surface)', border: '1px solid var(--c-border)',
          borderRadius: 10, minWidth: 160, boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: 6,
        }}>
          {BD_STATUS_VALUES.map((s) => (
            <button
              key={s}
              onClick={() => { setOpen(false); onSave(s); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: value === s ? 'var(--c-bg-2)' : 'transparent',
                fontSize: 12.5,
              }}
            >
              <span style={{ ...BD_STATUS_STYLE[s], borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                {s}
              </span>
            </button>
          ))}
          <button
            onClick={() => { setOpen(false); onSave(''); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'transparent', fontSize: 12, color: 'var(--c-text-3)' }}
          >
            ✕ Clear
          </button>
          <div style={{ borderTop: '1px solid var(--c-border)', marginTop: 4, paddingTop: 6, display: 'flex', gap: 4 }}>
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomSubmit(); }}
              placeholder="Custom…"
              style={{
                flex: 1, minWidth: 0, fontSize: 12, padding: '4px 6px', borderRadius: 6,
                border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text-1)',
              }}
            />
            <button
              onClick={handleCustomSubmit}
              disabled={!customText.trim()}
              style={{
                fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--c-border)',
                background: 'var(--c-bg-2)', cursor: customText.trim() ? 'pointer' : 'default',
                opacity: customText.trim() ? 1 : 0.5,
              }}
            >
              Set
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

function Tag({ children }) {
  return (
    <span className="badge badge-cold" style={{ whiteSpace: 'normal', lineHeight: 1.25, padding: '3px 8px', borderRadius: 999 }}>
      {children}
    </span>
  );
}

const TAG_COLOR_PALETTES = {
  job_function: [
    { background: 'rgba(99,102,241,.14)', color: '#4f46e5' },
    { background: 'rgba(168,85,247,.14)', color: '#7c3aed' },
    { background: 'rgba(14,165,233,.14)', color: '#0284c7' },
    { background: 'rgba(16,185,129,.14)', color: '#059669' },
  ],
  sector_coverage: [
    { background: 'rgba(14,165,233,.14)', color: '#0284c7' },
    { background: 'rgba(59,130,246,.14)', color: '#2563eb' },
    { background: 'rgba(6,182,212,.14)', color: '#0e7490' },
    { background: 'rgba(20,184,166,.14)', color: '#0f766e' },
  ],
  industry_focus: [
    { background: 'rgba(249,115,22,.14)', color: '#ea580c' },
    { background: 'rgba(245,158,11,.16)', color: '#b45309' },
    { background: 'rgba(236,72,153,.14)', color: '#db2777' },
    { background: 'rgba(139,92,246,.14)', color: '#7c3aed' },
  ],
  implied_sectors: [
    { background: 'rgba(16,185,129,.14)', color: '#059669' },
    { background: 'rgba(20,184,166,.14)', color: '#0f766e' },
    { background: 'rgba(34,197,94,.14)',  color: '#16a34a' },
    { background: 'rgba(132,204,22,.16)', color: '#4d7c0f' },
  ],
  sector_packs: [
    { background: 'rgba(99,102,241,.14)', color: '#4f46e5' },
    { background: 'rgba(139,92,246,.14)', color: '#7c3aed' },
    { background: 'rgba(168,85,247,.14)', color: '#9333ea' },
    { background: 'rgba(217,70,239,.14)', color: '#c026d3' },
  ],
};

function hashString(input = '') {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getTagTone(col, tag) {
  const palette = TAG_COLOR_PALETTES[col];
  if (!palette?.length) return null;
  return palette[hashString(`${col}:${String(tag).toLowerCase()}`) % palette.length];
}

function renderTagCell(value, col) {
  const tags = splitTags(value);
  if (tags.length === 0) return '—';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {tags.map((tag, idx) => {
        const tone = getTagTone(col, tag);
        return (
          <span
            key={`${tag}-${idx}`}
            className="badge"
            style={{
              whiteSpace: 'normal',
              lineHeight: 1.25,
              padding: '3px 8px',
              borderRadius: 999,
              ...(tone ? {
                background: tone.background,
                color: tone.color,
                border: '1px solid transparent',
              } : {
                background: 'var(--surface-2)',
                color: 'var(--text-2)',
              }),
            }}
          >
            {tag}
          </span>
        );
      })}
    </div>
  );
}

function formatAumBn(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const bn = n / 1000;
  return `$${bn.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}B`;
}

function deriveAumTier(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n < 100) return '<$100M';
  if (n < 500) return '$100M–$500M';
  if (n < 1000) return '$500M–$1B';
  if (n < 5000) return '$1B–$5B';
  return '$5B+';
}

const BACKEND_URL = import.meta.env.VITE_BACKEND_BASE_URL ?? '';

const STAGE_WEIGHTS = {
  customer: 1.0, trial: 0.6, demo: 0.3, warm: 0.1,
  cold: 0.05, 'cold-untouched': 0.02, dead: 0,
};
const PIPELINE_STAT_COLORS = { customer: 'green', trial: 'purple', demo: 'blue', warm: 'amber' };

function formatCurrencyFlexible(val) {
  const n = parseFloat(val) || 0;
  if (n === 0) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}B`;
  if (n >= 1)    return `$${n.toFixed(0)}M`;
  return `$${(n * 1000).toFixed(0)}K`;
}

const inputStyle = {
  display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 2,
  padding: '4px 8px', border: '1px solid var(--c-border)', borderRadius: 6,
  fontSize: 12, background: 'var(--c-bg-2)', color: 'var(--c-text)', outline: 'none',
};

function HeaderFilterDropdown({ col, type, table = 'contacts', selected, onChange, getToken, tagMode, onSetTagMode, otherFilters }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, flipUp: false });
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const searchRef = useRef(null);

  const isRange = type === 'range';
  const isDate  = type === 'date';
  const isMulti = !isRange && !isDate;

  // Facet options must reflect whatever OTHER filters are currently active
  // (e.g. status=approved should narrow the firm dropdown to firms with an
  // approved contact) — refetch whenever the rest of the filter set changes.
  const otherFiltersKey = JSON.stringify(otherFilters ?? []);
  const prevOtherFiltersKeyRef = useRef(otherFiltersKey);
  useEffect(() => {
    if (prevOtherFiltersKeyRef.current === otherFiltersKey) return;
    prevOtherFiltersKeyRef.current = otherFiltersKey;
    setOptions(null);
    if (open && isMulti) loadOptions();
  }, [otherFiltersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const safeSelected = isMulti
    ? (Array.isArray(selected) ? selected : [])
    : (selected && typeof selected === 'object' ? selected
        : isRange ? { min: '', max: '', includeNull: false, onlyHasVal: false } : { after: '', before: '', nullMode: 'all' });

  const isActive = isMulti
    ? safeSelected.length > 0
    : isDate
      ? ((safeSelected.nullMode !== 'all' && safeSelected.nullMode != null) || !!safeSelected.after || !!safeSelected.before)
      : isRange
        ? (!!safeSelected.includeNull || !!safeSelected.onlyHasVal || (safeSelected.min !== '' && safeSelected.min != null) || (safeSelected.max !== '' && safeSelected.max != null))
        : false;

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  useEffect(() => {
    if (open && isMulti && searchRef.current) searchRef.current.focus();
  }, [open, isMulti]);

  async function handleOpen() {
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < 340 && rect.top > 340;
      setPos({ top: flipUp ? rect.top - 4 : rect.bottom + 4, left: rect.left, flipUp });
    }
    setSearch('');
    if (isMulti && options === null && !loading) loadOptions();
    setOpen(true);
  }

  async function loadOptions() {
    setLoading(true);
    try {
      if (type === 'stage') {
        const ALL = PICKABLE_STAGES;
        setOptions(ALL.map((s) => ({ value: s, label: STAGE_LABELS[s] ?? s })));
        return;
      }
      if (type === 'status') {
        setOptions(BD_STATUS_VALUES.map((s) => ({ value: s, label: s })));
        return;
      }
      if (col === 'main_coverage') {
        setOptions([
          { value: 'null', label: 'Null' },
          { value: '0', label: '0' },
          { value: '1', label: '1' },
        ]);
        return;
      }
      if (col === '_bd_high_intent') {
        setOptions([
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
          ...BD_SIGNAL_OPTIONS,
        ]);
        return;
      }
      // Map col aliases to actual API param + which table to query.
      // _firm_owner/_fund_type/_firm_name are contacts-derived (joined) columns —
      // route those through /api/contacts/distinct so they respect the OTHER
      // active contact filters, instead of listing every firm in the whole table.
      let apiCol = col;
      let apiTable = table;
      if (col === '_firm_owner')      { apiCol = 'firm_owner'; }
      else if (col === '_fund_type')  { apiCol = 'fund_type'; }
      else if (col === '_firm_name')  { apiCol = 'firm_name'; }
      else if (col === 'fund_type' || col === 'firm_name' || col === 'owner' || col === 'hq_city') { apiTable = 'firms'; }

      const base = apiTable === 'firms'
        ? `${BACKEND_URL}/api/firms/distinct`
        : `${BACKEND_URL}/api/contacts/distinct`;
      const token = await getToken();
      const qs = new URLSearchParams({ col: apiCol });
      if (apiTable !== 'firms' && otherFilters?.length) qs.set('filters', JSON.stringify(otherFilters));
      const res = await fetch(`${base}?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const raw = data.values ?? [];
      if (type === 'tag') {
        const tags = new Set();
        raw.forEach((v) => String(v).split(',').forEach((t) => { const tt = t.trim(); if (tt) tags.add(tt); }));
        setOptions([...tags].sort().map((t) => ({ value: t, label: t })));
      } else {
        setOptions(raw.filter(Boolean).map((v) => ({ value: String(v), label: String(v) })));
      }
    } catch (_) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }

  function toggle(value) {
    onChange(safeSelected.includes(value) ? safeSelected.filter((v) => v !== value) : [...safeSelected, value]);
  }

  const showSearch = isMulti && !loading && options && options.length > 8;
  const filtered = showSearch && search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <span ref={wrapRef} style={{ display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        title={isActive ? 'Filtered' : 'Filter'}
        style={{
          background: isActive ? 'var(--c-primary)' : 'transparent',
          color: isActive ? '#fff' : 'var(--c-text-3)',
          border: 'none', borderRadius: 4, padding: '1px 5px',
          cursor: 'pointer', fontSize: 11, lineHeight: 1.5,
        }}
      >
        {isActive ? (isMulti ? `▾ ${safeSelected.length}` : '▾ •') : '▾'}
      </button>

      {open && (
        <div
          onWheel={(e) => e.stopPropagation()}
          onScroll={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: pos.flipUp ? undefined : pos.top,
            bottom: pos.flipUp ? window.innerHeight - pos.top : undefined,
            left: Math.min(pos.left, window.innerWidth - 240),
            zIndex: 9999,
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 10,
            minWidth: isRange || isDate ? 190 : 220,
            maxWidth: 300,
            boxShadow: '0 8px 32px rgba(0,0,0,.18)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: isRange || isDate ? 'none' : 360,
          }}
        >
          {isRange && (
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-2)' }}>
                Range filter{col === 'aum' ? ' ($B)' : ''}
              </div>
              {col === '_recent_touch_count' && (
                <label style={{ fontSize: 12, color: 'var(--c-text-2)' }}>Timeframe
                  <select
                    value={safeSelected.window || DEFAULT_TOUCH_WINDOW}
                    onChange={(e) => onChange({ ...safeSelected, window: e.target.value })}
                    style={{ ...inputStyle, width: '100%' }}
                  >
                    {TOUCH_WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              )}
              <label style={{ fontSize: 12, color: 'var(--c-text-2)' }}>Min
                <input type="number" value={safeSelected.min ?? ''} placeholder="No min"
                  onChange={(e) => onChange({ ...safeSelected, min: e.target.value })} style={inputStyle} />
              </label>
              <label style={{ fontSize: 12, color: 'var(--c-text-2)' }}>Max
                <input type="number" value={safeSelected.max ?? ''} placeholder="No max"
                  onChange={(e) => onChange({ ...safeSelected, max: e.target.value })} style={inputStyle} />
              </label>
              {isActive && <button onClick={() => { onChange({ min: '', max: '', includeNull: false, onlyHasVal: false }); setOpen(false); }}
                style={{ padding: '2px 6px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--c-text-3)', textAlign: 'right' }}>✕ Clear</button>}
            </div>
          )}

          {isDate && (
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-2)' }}>Date filter</div>
              <DateNullModeSelector
                mode={safeSelected.nullMode ?? 'all'}
                onChange={(nm) => onChange({ after: '', before: '', nullMode: nm })}
              />
              {(safeSelected.nullMode === 'all' || safeSelected.nullMode === 'include_no_date' || !safeSelected.nullMode) && (
                <>
                  <label style={{ fontSize: 12, color: 'var(--c-text-2)' }}>After
                    <input type="date" value={safeSelected.after ?? ''}
                      onChange={(e) => onChange({ ...safeSelected, after: e.target.value })} style={inputStyle} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--c-text-2)' }}>Before
                    <input type="date" value={safeSelected.before ?? ''}
                      onChange={(e) => onChange({ ...safeSelected, before: e.target.value })} style={inputStyle} />
                  </label>
                </>
              )}
              {isActive && <button onClick={() => { onChange({ after: '', before: '', nullMode: 'all' }); setOpen(false); }}
                style={{ padding: '2px 6px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--c-text-3)', textAlign: 'right' }}>✕ Clear</button>}
            </div>
          )}

          {isMulti && (
            <>
              {type === 'tag' && onSetTagMode && (
                <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid var(--c-border)', flexShrink: 0 }}>
                  <TagModeSelector mode={tagMode ?? 'any'} onChange={(m) => { onSetTagMode(m); }} />
                </div>
              )}
              {showSearch && (
                <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid var(--c-border)', flexShrink: 0 }}>
                  <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search…" style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--c-border)', borderRadius: 6, padding: '4px 8px', fontSize: 12, background: 'var(--c-bg-2)', color: 'var(--c-text)', outline: 'none' }} />
                </div>
              )}
              <div style={{ overflowY: 'auto', padding: 6, flex: 1 }}>
                {loading && <div style={{ padding: 8, color: 'var(--c-text-3)', fontSize: 12 }}>Loading…</div>}
                {!loading && !filtered?.length && <div style={{ padding: 8, color: 'var(--c-text-3)', fontSize: 12 }}>No options</div>}
                {filtered?.map((opt) => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px',
                    cursor: 'pointer', borderRadius: 5, fontSize: 12.5,
                    background: safeSelected.includes(opt.value) ? 'var(--c-bg-2)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={safeSelected.includes(opt.value)} onChange={() => toggle(opt.value)} style={{ margin: 0, flexShrink: 0 }} />
                    {type === 'stage' ? (
                      <span className={`badge ${STAGE_BADGE_CLASS[opt.value] ?? 'badge-cold'}`}>{opt.label}</span>
                    ) : type === 'status' ? (
                      <span style={{ ...BD_STATUS_STYLE[opt.value], borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{opt.label}</span>
                    ) : col === 'main_coverage' ? (
                      <span style={{
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        background: opt.value === 'null' ? 'var(--c-bg-2)' : opt.value === '1' ? 'rgba(34,197,94,.12)' : 'rgba(249,115,22,.12)',
                        color: opt.value === 'null' ? 'var(--c-text-2)' : opt.value === '1' ? 'rgb(22,101,52)' : 'rgb(154,52,18)',
                      }}>{opt.label}</span>
                    ) : (
                      <span style={{ fontSize: 12 }}>{opt.label}</span>
                    )}
                  </label>
                ))}
              </div>
              {isActive && (
                <div style={{ borderTop: '1px solid var(--c-border)', padding: '5px 8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>{safeSelected.length} selected</span>
                  <button onClick={() => { onChange([]); setOpen(false); }}
                    style={{ padding: '2px 8px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--c-text-3)' }}>
                    ✕ Clear
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </span>
  );
}

const DATE_NULL_MODES = [
  { key: 'all',             label: 'All',       title: 'Show all rows; use After/Before to filter by date range (null rows excluded when range is set)' },
  { key: 'has_date',        label: 'Has date',  title: 'Only show rows that have a date (exclude rows with no date)' },
  { key: 'no_date',         label: 'No date',   title: 'Only show rows with no date (null)' },
  { key: 'include_no_date', label: '+ No date', title: 'Include rows with no date alongside the After/Before range — e.g. "before 06/01 OR no date"' },
];

function DateNullModeSelector({ mode, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {DATE_NULL_MODES.map((m) => (
        <button
          key={m.key}
          title={m.title}
          onClick={() => onChange(m.key)}
          style={{
            padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: mode === m.key ? 'var(--c-primary)' : 'transparent',
            color: mode === m.key ? '#fff' : 'var(--c-text-3)',
            border: mode === m.key ? 'none' : '1px solid var(--c-border)',
          }}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

const TAG_MODES = [
  { key: 'any',    label: 'Any',    title: 'Show contacts who have at least one of the selected tags (OR)' },
  { key: 'all',    label: 'All',    title: 'Show contacts who have ALL of the selected tags (AND)' },
  { key: 'within', label: 'Within', title: 'Show contacts whose tags are all within the selected set — no tags outside it allowed (e.g. select Banks+Financials: show Banks, Financials, Banks+Financials — but NOT Banks+Tech)' },
  { key: 'none',   label: 'None',   title: 'Exclude contacts who have any of the selected tags (NOT)' },
  { key: 'only',   label: 'Only',   title: 'Show contacts whose tags are exactly the selected set (no more, no less)' },
];

function TagModeSelector({ mode, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
      <span style={{ fontSize: 10.5, color: 'var(--c-text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2 }}>Match</span>
      {TAG_MODES.map((m) => (
        <button
          key={m.key}
          title={m.title}
          onClick={() => onChange(m.key)}
          style={{
            padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: mode === m.key ? 'var(--c-primary)' : 'transparent',
            color: mode === m.key ? '#fff' : 'var(--c-text-3)',
            border: mode === m.key ? 'none' : '1px solid var(--c-border)',
          }}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function TextModeToggle({ exclude, onToggle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
      <span style={{ fontSize: 10.5, color: 'var(--c-text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2 }}>Mode</span>
      {[{ key: false, label: 'Include' }, { key: true, label: 'Exclude' }].map(({ key, label }) => (
        <button
          key={label}
          onClick={() => { if (exclude !== key) onToggle(); }}
          title={key ? 'Show contacts NOT matching any selected value' : 'Show contacts matching any selected value'}
          style={{
            padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: exclude === key ? (key ? 'rgba(239,68,68,.85)' : 'var(--c-primary)') : 'transparent',
            color: exclude === key ? '#fff' : 'var(--c-text-3)',
            border: exclude === key ? 'none' : '1px solid var(--c-border)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function FilterSection({ col, type, table, selected, onChange, getToken, label, tagMode, onSetTagMode, textExclude, onToggleTextExclude, otherFilters }) {
  const isRange = type === 'range';
  const isDate  = type === 'date';
  const isMulti = !isRange && !isDate;

  const safeSelected = isMulti
    ? (Array.isArray(selected) ? selected : [])
    : (selected && typeof selected === 'object' ? selected
        : isRange ? { min: '', max: '', includeNull: false, onlyHasVal: false } : { after: '', before: '', nullMode: 'all' });

  const activeCount = isMulti
    ? safeSelected.length
    : isDate
      ? ((safeSelected.nullMode !== 'all' && safeSelected.nullMode != null ? 1 : 0) + (safeSelected.after ? 1 : 0) + (safeSelected.before ? 1 : 0))
      : isRange
        ? ((safeSelected.includeNull ? 1 : 0) + (safeSelected.onlyHasVal ? 1 : 0) + (safeSelected.min ? 1 : 0) + (safeSelected.max ? 1 : 0))
        : 0;

  const [expanded, setExpanded] = useState(() => activeCount > 0);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Facet options must reflect whatever OTHER filters are currently active
  // (e.g. status=approved should narrow the firm dropdown to firms with an
  // approved contact) — refetch whenever the rest of the filter set changes,
  // but not on every plain expand/collapse toggle.
  const otherFiltersKey = JSON.stringify(otherFilters ?? []);
  const loadedKeyRef = useRef(null);

  useEffect(() => {
    if (!expanded || !isMulti) return;
    if (options !== null && loadedKeyRef.current === otherFiltersKey) return;
    loadedKeyRef.current = otherFiltersKey;
    loadOptions();
  }, [expanded, otherFiltersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadOptions() {
    setLoading(true);
    try {
      if (type === 'stage') {
        const ALL = PICKABLE_STAGES;
        setOptions(ALL.map((s) => ({ value: s, label: STAGE_LABELS[s] ?? s })));
        return;
      }
      if (type === 'status') {
        setOptions(BD_STATUS_VALUES.map((s) => ({ value: s, label: s })));
        return;
      }
      if (col === 'main_coverage') {
        setOptions([{ value: 'null', label: 'Null' }, { value: '0', label: '0' }, { value: '1', label: '1' }]);
        return;
      }
      if (col === '_bd_high_intent') {
        setOptions([{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }, ...BD_SIGNAL_OPTIONS]);
        return;
      }
      // _firm_owner/_fund_type/_firm_name are contacts-derived (joined) columns —
      // route those through /api/contacts/distinct so they respect the OTHER
      // active contact filters, instead of listing every firm in the whole table.
      let apiCol = col;
      let apiTable = table;
      if (col === '_firm_owner')      { apiCol = 'firm_owner'; }
      else if (col === '_fund_type')  { apiCol = 'fund_type'; }
      else if (col === '_firm_name')  { apiCol = 'firm_name'; }
      else if (col === 'fund_type' || col === 'firm_name' || col === 'owner' || col === 'hq_city') { apiTable = 'firms'; }

      const base = apiTable === 'firms'
        ? `${BACKEND_URL}/api/firms/distinct`
        : `${BACKEND_URL}/api/contacts/distinct`;
      const token = await getToken();
      const qs = new URLSearchParams({ col: apiCol });
      if (apiTable !== 'firms' && otherFilters?.length) qs.set('filters', JSON.stringify(otherFilters));
      const res = await fetch(`${base}?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const raw = data.values ?? [];
      if (type === 'tag') {
        const tags = new Set();
        raw.forEach((v) => String(v).split(',').forEach((t) => { const tt = t.trim(); if (tt) tags.add(tt); }));
        setOptions([...tags].sort().map((t) => ({ value: t, label: t })));
      } else {
        setOptions(raw.filter(Boolean).map((v) => ({ value: String(v), label: String(v) })));
      }
    } catch (_) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }

  function toggle(value) {
    onChange(safeSelected.includes(value) ? safeSelected.filter((v) => v !== value) : [...safeSelected, value]);
  }

  // Boolean columns get bare Yes/No checkboxes — no search, no select-all,
  // no include/exclude mode (excluding Yes is just including No).
  const isBool = col === '_bd_high_intent';
  const pillLabel = (v) => (isBool ? (v === 'true' ? 'Yes' : v === 'false' ? 'No' : BD_SIGNAL_LABELS[v] ?? v) : v);
  // Always show search for multi-select with options (even <8) so typing works
  const showSearch = isMulti && !loading && options && options.length > 0 && !isBool;
  const trimmedSearch = search.trim().toLowerCase();
  const filtered = (isMulti && trimmedSearch)
    ? options.filter((o) => o.label.toLowerCase().includes(trimmedSearch))
    : options;

  // Select all currently-visible filtered results
  const filteredValues = filtered?.map((o) => o.value) ?? [];
  const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((v) => safeSelected.includes(v));
  const someFilteredSelected = !allFilteredSelected && filteredValues.some((v) => safeSelected.includes(v));

  function selectAllFiltered() {
    const next = [...new Set([...safeSelected, ...filteredValues])];
    onChange(next);
  }
  function deselectAllFiltered() {
    onChange(safeSelected.filter((v) => !filteredValues.includes(v)));
  }

  const headerBg = activeCount > 0 ? 'rgba(var(--c-primary-rgb,59,130,246),.05)' : 'transparent';

  return (
    <div style={{ borderBottom: '1px solid var(--c-border)' }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
          background: headerBg, textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 600, color: activeCount > 0 ? 'var(--c-primary)' : 'var(--c-text)', flex: 1 }}>{label}</span>
        {activeCount > 0 && (
          <span style={{ background: 'var(--c-primary)', color: '#fff', borderRadius: 999, fontSize: 10.5, fontWeight: 700, padding: '1px 7px', marginRight: 6, flexShrink: 0 }}>
            {activeCount}
          </span>
        )}
        <span style={{ color: 'var(--c-text-3)', fontSize: 11 }}>{expanded ? '▾' : '›'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 12px 10px' }}>
          {/* Selected pills */}
          {isMulti && safeSelected.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, paddingTop: 4 }}>
              {safeSelected.map((v) => (
                <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'var(--c-primary)', color: '#fff', borderRadius: 999, fontSize: 11, padding: '2px 8px', maxWidth: 180, overflow: 'hidden' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pillLabel(v)}</span>
                  <button onClick={() => toggle(v)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 13, flexShrink: 0 }}>×</button>
                </span>
              ))}
              <button onClick={() => onChange([])} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--c-text-3)', padding: '2px 4px', alignSelf: 'center' }}>Clear all</button>
            </div>
          )}

          {isRange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Touches counts within a pickable window — the timeframe also
                  re-windows the table column (see buildFilters' window marker). */}
              {col === '_recent_touch_count' && (
                <label style={{ fontSize: 11.5, color: 'var(--c-text-2)', paddingTop: 4 }}>Timeframe
                  <select
                    value={safeSelected.window || DEFAULT_TOUCH_WINDOW}
                    onChange={(e) => onChange({ ...safeSelected, window: e.target.value })}
                    style={{ ...inputStyle, marginTop: 2, width: '100%' }}
                  >
                    {TOUCH_WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              )}
              {/* AUM columns are stored in $M but people quote them in B —
                  unit toggle scales the entered numbers (see buildFilters). */}
              {AUM_UNIT_COLS.has(col) && (
                <div style={{ display: 'flex', gap: 4, paddingTop: 4 }}>
                  {['M', 'B'].map((u) => {
                    const active = (safeSelected.unit ?? 'B') === u;
                    return (
                      <button key={u} title={`Enter values in ${u === 'B' ? 'billions' : 'millions'}`}
                        onClick={() => onChange({ ...safeSelected, unit: u })}
                        style={{
                          padding: '2px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          background: active ? 'var(--c-primary)' : 'transparent',
                          color: active ? '#fff' : 'var(--c-text-3)',
                          border: active ? 'none' : '1px solid var(--c-border)',
                        }}
                      >
                        ${u}
                      </button>
                    );
                  })}
                </div>
              )}
              {!safeSelected.onlyHasVal && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="number" placeholder={AUM_UNIT_COLS.has(col) ? `Min ($${safeSelected.unit ?? 'B'})` : 'Min'} value={safeSelected.min ?? ''}
                    onChange={(e) => onChange({ ...safeSelected, min: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                  <span style={{ color: 'var(--c-text-3)', fontSize: 12 }}>—</span>
                  <input type="number" placeholder={AUM_UNIT_COLS.has(col) ? `Max ($${safeSelected.unit ?? 'B'})` : 'Max'} value={safeSelected.max ?? ''}
                    onChange={(e) => onChange({ ...safeSelected, max: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[
                  { key: 'onlyHasVal',  label: 'Has value',     title: 'Only show rows that have a value (exclude empty/null)' },
                  { key: 'includeNull', label: '+ No value',    title: 'Include rows with no value alongside the min/max range' },
                ].map(({ key, label, title }) => (
                  <button key={key} title={title}
                    onClick={() => onChange({ ...safeSelected, min: key === 'onlyHasVal' ? '' : safeSelected.min, max: key === 'onlyHasVal' ? '' : safeSelected.max, [key]: !safeSelected[key], ...(key === 'onlyHasVal' ? { includeNull: false } : { onlyHasVal: false }) })}
                    style={{
                      padding: '2px 9px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      background: safeSelected[key] ? 'var(--c-primary)' : 'transparent',
                      color: safeSelected[key] ? '#fff' : 'var(--c-text-3)',
                      border: safeSelected[key] ? 'none' : '1px solid var(--c-border)',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {(safeSelected.min || safeSelected.max || safeSelected.includeNull || safeSelected.onlyHasVal) && (
                <button onClick={() => onChange({ min: '', max: '', includeNull: false, onlyHasVal: false })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--c-text-3)', textAlign: 'right', padding: 0 }}>✕ Clear</button>
              )}
            </div>
          )}

          {isDate && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <DateNullModeSelector
                mode={safeSelected.nullMode ?? 'all'}
                onChange={(nm) => onChange({ after: '', before: '', nullMode: nm })}
              />
              {(safeSelected.nullMode === 'all' || safeSelected.nullMode === 'include_no_date' || !safeSelected.nullMode) && (
                <>
                  <label style={{ fontSize: 11.5, color: 'var(--c-text-2)' }}>After
                    <input type="date" value={safeSelected.after ?? ''}
                      onChange={(e) => onChange({ ...safeSelected, after: e.target.value })} style={{ ...inputStyle, marginTop: 2 }} />
                  </label>
                  <label style={{ fontSize: 11.5, color: 'var(--c-text-2)' }}>Before
                    <input type="date" value={safeSelected.before ?? ''}
                      onChange={(e) => onChange({ ...safeSelected, before: e.target.value })} style={{ ...inputStyle, marginTop: 2 }} />
                  </label>
                </>
              )}
              {((safeSelected.nullMode ?? 'all') !== 'all' || safeSelected.after || safeSelected.before) && (
                <button onClick={() => onChange({ after: '', before: '', nullMode: 'all' })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--c-text-3)', textAlign: 'right', padding: 0 }}>✕ Clear</button>
              )}
            </div>
          )}

          {isMulti && (
            <>
              {/* Tag mode selector — always shown at top for tag columns */}
              {type === 'tag' && onSetTagMode && (
                <div style={{ marginBottom: 6 }}>
                  <TagModeSelector mode={tagMode ?? 'any'} onChange={onSetTagMode} />
                </div>
              )}

              {/* Search input — always shown when options exist */}
              {showSearch && (
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  style={{ ...inputStyle, marginBottom: 2 }}
                />
              )}

              {/* "Select all N matching" bar — shown when search is active and has results */}
              {trimmedSearch && filtered && filtered.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 2px 6px', borderBottom: '1px solid var(--c-border)', marginBottom: 4 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--c-text-2)' }}>
                    {filtered.length} result{filtered.length === 1 ? '' : 's'} for <strong>"{search.trim()}"</strong>
                  </span>
                  <button
                    onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered}
                    style={{
                      background: allFilteredSelected ? 'transparent' : 'var(--c-primary)',
                      color: allFilteredSelected ? 'var(--c-text-3)' : '#fff',
                      border: allFilteredSelected ? '1px solid var(--c-border)' : 'none',
                      borderRadius: 6, padding: '2px 9px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    {allFilteredSelected ? 'Deselect all' : `Select all ${filtered.length}`}
                  </button>
                </div>
              )}

              {/* No search active — "Select all / Deselect all" for full list */}
              {!trimmedSearch && !isBool && options && options.length > 0 && options.length <= 30 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                  {!allFilteredSelected && (
                    <button onClick={selectAllFiltered} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--c-primary)', padding: '2px 0' }}>
                      Select all
                    </button>
                  )}
                  {safeSelected.length > 0 && (
                    <button onClick={() => onChange([])} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--c-text-3)', padding: '2px 0' }}>
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* Text include/exclude toggle */}
              {type === 'text' && !isBool && onToggleTextExclude && safeSelected.length > 0 && (
                <TextModeToggle exclude={textExclude} onToggle={onToggleTextExclude} />
              )}

              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {loading && <div style={{ padding: '6px 0', color: 'var(--c-text-3)', fontSize: 12 }}>Loading…</div>}
                {!loading && filtered?.length === 0 && <div style={{ padding: '6px 0', color: 'var(--c-text-3)', fontSize: 12 }}>No results for "{search}"</div>}
                {filtered?.map((opt) => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px', cursor: 'pointer', borderRadius: 5, fontSize: 12.5, background: safeSelected.includes(opt.value) ? 'rgba(var(--c-primary-rgb,59,130,246),.08)' : 'transparent' }}>
                    <input type="checkbox" checked={safeSelected.includes(opt.value)} onChange={() => toggle(opt.value)} style={{ margin: 0, flexShrink: 0 }} />
                    {type === 'stage' ? (
                      <span className={`badge ${STAGE_BADGE_CLASS[opt.value] ?? 'badge-cold'}`}>{opt.label}</span>
                    ) : type === 'status' ? (
                      <span style={{ ...BD_STATUS_STYLE[opt.value], borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{opt.label}</span>
                    ) : (
                      <span style={{ fontSize: 12 }}>{opt.label}</span>
                    )}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FilterPanel({ view, colFilters, firmColFilters, onColFilterChange, onFirmColFilterChange, onClearAll, onClose, getToken, tagFilterModes, onSetTagMode, textExcludeCols, onToggleTextExclude, getFilters }) {
  const groups = view === 'firms' ? FIRM_FILTER_GROUPS : CONTACT_FILTER_GROUPS;
  const filters = view === 'firms' ? firmColFilters : colFilters;
  const onFilterChange = view === 'firms' ? onFirmColFilterChange : onColFilterChange;
  const filterCols = view === 'firms' ? FIRM_HEADER_FILTER_COLS : HEADER_FILTER_COLS;
  // Only the contacts view's dropdowns cascade off the other active filters —
  // getFilters is omitted for the firms view, where each column already queries
  // its own table directly.
  const allFilters = getFilters ? getFilters() : null;

  const totalActive = Object.values(filters).reduce((sum, v) => {
    if (Array.isArray(v)) return sum + (v.length > 0 ? 1 : 0);
    // 'window'/'unit' are display modifiers, not filter values — alone they
    // exclude no rows, so they don't count toward the active badge.
    if (v && typeof v === 'object') return sum + (Object.entries(v).some(([k, x]) => k !== 'window' && k !== 'unit' && x && String(x).trim()) ? 1 : 0);
    return sum;
  }, 0);

  function getDefaultSelected(type) {
    if (type === 'range') return { min: '', max: '' };
    if (type === 'date') return { after: '', before: '' };
    return [];
  }

  return (
    <div style={{
      width: 258, flexShrink: 0,
      background: 'var(--c-surface)',
      border: '1px solid var(--c-border)',
      borderRadius: 10,
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 140px)',
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      marginRight: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px 10px', borderBottom: '1px solid var(--c-border)',
        position: 'sticky', top: 0, background: 'var(--c-surface)', zIndex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontWeight: 700, fontSize: 13.5 }}>Filters</span>
          {totalActive > 0 && (
            <span style={{ background: 'var(--c-primary)', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>
              {totalActive}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {totalActive > 0 && (
            <button onClick={onClearAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--c-text-3)', padding: '2px 6px', borderRadius: 5 }}>
              Clear all
            </button>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--c-text-3)', lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>
      </div>

      {groups.map((group) => (
        <div key={group.label}>
          <div style={{ padding: '8px 14px 2px', fontSize: 10.5, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {group.label}
          </div>
          {group.cols.map((col) => {
            const type = filterCols[col] ?? 'text';
            return (
              <FilterSection
                key={col}
                col={col}
                type={type}
                table={view === 'firms' ? 'firms' : 'contacts'}
                selected={filters[col] ?? getDefaultSelected(type)}
                onChange={(vals) => onFilterChange(col, vals)}
                getToken={getToken}
                label={compactLabel(col)}
                tagMode={tagFilterModes?.[col] ?? 'any'}
                onSetTagMode={(mode) => onSetTagMode?.(col, mode)}
                textExclude={textExcludeCols?.has(col) ?? false}
                onToggleTextExclude={() => onToggleTextExclude?.(col)}
                otherFilters={allFilters ? allFilters.filter((f) => f.col !== col) : []}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

function StageCounts({ counts = {} }) {
  if (!counts) return '—';
  const STAGE_ORDER = ['customer','trial','user','demo','warm','cold','dead','cold-untouched'];
  const entries = STAGE_ORDER.map((s) => ({ s, n: Number(counts[s] ?? 0) })).filter((e) => e.n > 0);
  if (entries.length === 0) return <span style={{ color: 'var(--c-text-3)', fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {entries.map(({ s, n }) => (
        <span key={s} className={`badge ${STAGE_BADGE_CLASS[s] ?? 'badge-cold'}`} style={{ fontSize: 11 }}>
          {STAGE_LABELS[s]} ({n})
        </span>
      ))}
    </div>
  );
}

// ── Saved Views helpers ───────────────────────────────────────────────────────

const PIPELINE_VIEW_ID = '__pipeline__';

function canonState(colFilters, appliedFilters, sortKey, sortDir, columns, tagFilterModes, textExcludeCols) {
  // Produce a stable string for deep-equality comparison
  function c(v) {
    if (Array.isArray(v)) return [...v].sort().join('|');
    if (v && typeof v === 'object') return Object.keys(v).sort().map((k) => `${k}:${c(v[k])}`).join(',');
    return String(v ?? '');
  }
  const cf = Object.keys(colFilters).sort().map((k) => `${k}:${c(colFilters[k])}`).join(';');
  const af = (appliedFilters ?? []).map((f) => `${f.col}:${f.op}:${f.val}`).sort().join(';');
  const cols = [...(columns ?? [])].sort().join(',');
  const tmodes = Object.keys(tagFilterModes ?? {}).sort().map((k) => `${k}:${tagFilterModes[k]}`).join(',');
  const texcl = [...(textExcludeCols ?? [])].sort().join(',');
  return `cf:${cf}|af:${af}|sk:${sortKey ?? ''}|sd:${sortDir ?? 'asc'}|cols:${cols}|tm:${tmodes}|te:${texcl}`;
}

function ViewDropdown({ views, activeViewId, dirty, onSelect, onSaveNew, onUpdateCurrent, onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const allViews = [{ id: PIPELINE_VIEW_ID, name: 'Pipeline', builtIn: true }, ...views];
  const active = allViews.find((v) => v.id === activeViewId) ?? allViews[0];

  function startRename(v, e) {
    e.stopPropagation();
    setRenamingId(v.id);
    setRenameVal(v.name);
  }
  function commitRename(id) {
    if (renameVal.trim()) onRename(id, renameVal.trim());
    setRenamingId(null);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 12px', borderRadius: 8, border: '1px solid var(--c-border)',
          background: open ? 'var(--c-bg-2)' : 'var(--c-surface)',
          cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--c-text)',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 14 }}>⊞</span>
        <span>{active.name}</span>
        {dirty && <span style={{ color: 'var(--c-primary)', fontWeight: 700, fontSize: 15, lineHeight: 1 }}>*</span>}
        <span style={{ color: 'var(--c-text-3)', fontSize: 11 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999,
          background: 'var(--c-surface)', border: '1px solid var(--c-border)',
          borderRadius: 10, minWidth: 220, maxWidth: 320,
          boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: '6px 0', overflow: 'hidden',
        }}>
          {/* View list */}
          {allViews.map((v) => (
            <div
              key={v.id}
              onClick={() => { if (renamingId === v.id) return; onSelect(v); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', cursor: 'pointer',
                background: v.id === activeViewId ? 'var(--c-bg-2)' : 'transparent',
                fontSize: 13,
              }}
              onMouseEnter={(e) => { if (v.id !== activeViewId) e.currentTarget.style.background = 'var(--c-bg-2)'; }}
              onMouseLeave={(e) => { if (v.id !== activeViewId) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ width: 14, flexShrink: 0, color: 'var(--c-primary)', fontWeight: 700 }}>
                {v.id === activeViewId ? '✓' : ''}
              </span>
              {renamingId === v.id ? (
                <input
                  autoFocus
                  value={renameVal}
                  onChange={(e) => setRenameVal(e.target.value)}
                  onBlur={() => commitRename(v.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitRename(v.id); if (e.key === 'Escape') setRenamingId(null); }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ flex: 1, padding: '2px 6px', fontSize: 13, border: '1px solid var(--c-primary)', borderRadius: 5, outline: 'none', background: 'var(--c-surface)' }}
                />
              ) : (
                <span style={{ flex: 1, fontWeight: v.id === activeViewId ? 600 : 400, color: 'var(--c-text)' }}>{v.name}</span>
              )}
              {!v.builtIn && renamingId !== v.id && (
                <span style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  <button onClick={(e) => startRename(v, e)} title="Rename" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px', fontSize: 12, color: 'var(--c-text-3)', borderRadius: 4 }}>✎</button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(v.id); setOpen(false); }} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px', fontSize: 12, color: 'var(--c-text-3)', borderRadius: 4 }}>✕</button>
                </span>
              )}
            </div>
          ))}

          {/* Actions */}
          <div style={{ borderTop: '1px solid var(--c-border)', marginTop: 4, paddingTop: 4 }}>
            {dirty && activeViewId !== PIPELINE_VIEW_ID && (
              <button
                onClick={() => { onUpdateCurrent(); setOpen(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--c-text)', textAlign: 'left' }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--c-bg-2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <span style={{ color: 'var(--c-primary)' }}>↺</span>
                <span>Update "{active.name}"</span>
              </button>
            )}
            <button
              onClick={() => { onSaveNew(); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--c-text)', textAlign: 'left' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--c-bg-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              <span style={{ color: 'var(--c-primary)' }}>☆</span>
              <span>Save as new view…</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// High Intent chip with an instant styled tooltip explaining the signal
// ("Opened email 4×", "Clicked link"). The native title tooltip needs a ~1s
// hover before it shows, which buried the "why". Same fixed-position pattern
// as UsageHeatmap's cell tooltip.
function HighIntentChip({ row }) {
  const [tip, setTip] = useState(null); // {x, y} | null
  const signals = [
    Number(row._bd_read_count || 0) > 0 ? `Opened email ${row._bd_read_count}×` : null,
    Number(row._bd_url_link_views || 0) > 0 ? `Clicked link ${row._bd_url_link_views}×` : null,
    Number(row._bd_file_link_views || 0) > 0 ? `Viewed file ${row._bd_file_link_views}×` : null,
  ].filter(Boolean);
  return (
    <>
      <span
        onMouseEnter={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setTip({ x: r.left + r.width / 2, y: r.top - 6 });
        }}
        onMouseLeave={() => setTip(null)}
        style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
          background: 'var(--red-bg, #fee2e2)', color: 'var(--red, #dc2626)', whiteSpace: 'nowrap', cursor: 'default' }}
      >
        🔥 High intent
      </span>
      {tip && (
        <div style={{
          position: 'fixed', left: tip.x, top: tip.y, transform: 'translate(-50%, -100%)',
          background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8,
          padding: '7px 10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10000,
          fontSize: 11.5, pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          {signals.length > 0
            ? signals.map((s) => <div key={s} style={{ color: 'var(--c-text)', fontWeight: 600, lineHeight: 1.5 }}>{s}</div>)
            : <div style={{ color: 'var(--c-text)', fontWeight: 600 }}>BD engagement</div>}
        </div>
      )}
    </>
  );
}

const BIGDOUGH_DEFAULT_COLS = [
  'name', 'email', 'firm_name', 'sector_coverage', 'industry_focus',
  'main_coverage', 'job_function', 'focus_region', 'institution_type',
  'focus_strategy', 'account_aum', '_firm_aum', '_last_touch_ts',
  '_touches', '_last_email_open_ts', '_recent_touch_count', '_stage',
];

const BIGDOUGH_COL_LABELS = {
  name: 'Name',
  email: 'Email',
  firm_name: 'Account Name',
  sector_coverage: 'Sector Coverage',
  industry_focus: 'Industry Focus',
  main_coverage: 'Main Coverage',
  job_function: 'Job Function',
  focus_region: 'Focus Region',
  institution_type: 'Institution Type',
  focus_strategy: 'Focus Strategy',
  account_aum: 'Account AUM',
  _firm_aum: 'Firm AUM',
  _last_touch_ts: 'Last Touch',
  _touches: 'Touches',
  _last_email_open_ts: 'Last Email Open',
  _recent_touch_count: 'Recent Touch Count',
  _stage: 'Stage',
  ...CONTACT_LABELS,
  bd_status: 'Status',
  role: 'Role',
  fund_type: 'Fund Type',
  relevance_score: 'Relevance Score',
  added_date: 'Added Date',
  city: 'City',
  state_province: 'State/Province',
  country_territory: 'Country',
  side: 'Side',
  asset_class: 'Asset Class',
  dominant_orientation: 'Dom. Orientation',
  dominant_style: 'Dom. Style',
  focus_orientation: 'Focus Orientation',
  focus_orientation_secondary: 'Focus Orientation (EQ)',
  focus_industry_macro: 'Focus Industry Macro',
  focus_industry_mid: 'Focus Industry Mid',
  focus_industry_micro: 'Focus Industry Micro',
  focus_market_cap: 'Focus Mkt Cap',
  focus_style: 'Focus Style',
};

const BIGDOUGH_ALL_COLS = [
  ...BIGDOUGH_DEFAULT_COLS,
  'bd_status', 'role', 'fund_type', 'relevance_score', 'added_date',
  'city', 'state_province', 'country_territory', 'side', 'asset_class',
  'dominant_orientation', 'dominant_style', 'focus_orientation',
  'focus_orientation_secondary', 'focus_industry_macro', 'focus_industry_mid',
  'focus_industry_micro', 'focus_market_cap', 'focus_style',
];

function BigDoughExportModal({ onClose, onExport }) {
  const [selected, setSelected] = useState(new Set(BIGDOUGH_DEFAULT_COLS));

  function toggle(col) {
    const next = new Set(selected);
    if (next.has(col)) next.delete(col); else next.add(col);
    setSelected(next);
  }

  const optionalCols = BIGDOUGH_ALL_COLS.filter((c) => !BIGDOUGH_DEFAULT_COLS.includes(c));

  return (
    <div className="panel-overlay" onClick={onClose} style={{ zIndex: 10001 }}>
      <div
        className="panel"
        style={{ width: 480, maxWidth: '95vw', height: 'auto', maxHeight: '90vh', margin: 'auto', borderRadius: 14, display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel__header">
          <div className="panel__title">Export for BigDough</div>
          <button className="panel__close" onClick={onClose}>×</button>
        </div>
        <div className="panel__body" style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 14 }}>
            Each selected contact will have an <strong>Approved</strong> column (Y/N based on status) prepended. Default columns are pre-selected; add more below.
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Default columns</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
            {BIGDOUGH_DEFAULT_COLS.map((col) => (
              <label key={col} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: selected.has(col) ? 'rgba(59,130,246,.1)' : 'var(--c-bg-2)', border: `1px solid ${selected.has(col) ? 'rgba(59,130,246,.4)' : 'var(--c-border)'}`, borderRadius: 6, padding: '4px 9px', cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={selected.has(col)} onChange={() => toggle(col)} style={{ margin: 0 }} />
                {BIGDOUGH_COL_LABELS[col] ?? col}
              </label>
            ))}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Additional columns</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {optionalCols.map((col) => (
              <label key={col} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: selected.has(col) ? 'rgba(59,130,246,.1)' : 'var(--c-bg-2)', border: `1px solid ${selected.has(col) ? 'rgba(59,130,246,.4)' : 'var(--c-border)'}`, borderRadius: 6, padding: '4px 9px', cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={selected.has(col)} onChange={() => toggle(col)} style={{ margin: 0 }} />
                {BIGDOUGH_COL_LABELS[col] ?? col}
              </label>
            ))}
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--c-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={() => onExport([...selected])}>
            Export CSV
          </button>
        </div>
      </div>
    </div>
  );
}

function SaveViewModal({ initialName, onSave, onCancel, saving }) {
  const [name, setName] = useState(initialName ?? '');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="panel-overlay" onClick={onCancel} style={{ zIndex: 10001 }}>
      <div
        className="panel"
        style={{ width: 380, maxWidth: '95vw', height: 'auto', margin: 'auto', borderRadius: 14 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel__header">
          <div className="panel__title">Save view</div>
          <button className="panel__close" onClick={onCancel}>×</button>
        </div>
        <div className="panel__body">
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginBottom: 12 }}>
            Give this filter combination a name. It'll appear in the Views dropdown for everyone.
          </div>
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onSave(name.trim()); if (e.key === 'Escape') onCancel(); }}
            placeholder="e.g. Healthcare Analysts"
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '8px 12px', fontSize: 14, border: '1px solid var(--c-border)', borderRadius: 8, background: 'var(--c-bg-2)', color: 'var(--c-text)', outline: 'none', marginBottom: 16 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn--ghost btn--sm" onClick={onCancel} disabled={saving}>Cancel</button>
            <button className="btn btn--primary btn--sm" onClick={() => name.trim() && onSave(name.trim())} disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : 'Save view'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Contacts({ data, savedContactPing, onOpenTimeline, onOpenAddContact, onEditContact, getToken, onRefresh, highlightContact, onClearHighlight, amplitudeScores, settings = {} }) {
  const [contactColWidths, saveContactColWidths] = useColumnResize('contacts_col_widths');
  const [firmColWidths, saveFirmColWidths] = useColumnResize('firms_col_widths');
  const contactHeaderCheckRef = useRef(null);
  const firmHeaderCheckRef = useRef(null);
  const highlightRowRef = useRef(null);
  const [flashContactId, setFlashContactId] = useState(null);

  function handleContactResize(col, w) {
    const next = { ...contactColWidths, [col]: w };
    saveContactColWidths(next);
  }
  function handleFirmResize(col, w) {
    const next = { ...firmColWidths, [col]: w };
    saveFirmColWidths(next);
  }

  // ── Saved views ──────────────────────────────────────────────────────────────
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(PIPELINE_VIEW_ID);
  const [saveModal, setSaveModal] = useState(false);      // true = open
  const [savingView, setSavingView] = useState(false);
  const [viewSaveMsg, setViewSaveMsg] = useState(null);  // { ok, text } — inline feedback
  const [autoToast, setAutoToast] = useState(false);      // "want to save?" toast
  const [autoToastDismissed, setAutoToastDismissed] = useState(false);
  const [trialCreatedToast, setTrialCreatedToast] = useState(null); // { endDate } | null
  // ─────────────────────────────────────────────────────────────────────────────

  const [view, setView] = useState('contacts');
  const [firmLayout, setFirmLayout] = useState('list');
  const [contacts, setContacts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [firmsTotal, setFirmsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  // Server queries key off the debounced value so typing doesn't fire a
  // network fetch (and a table + pipeline-stats re-render) per keystroke.
  // The raw `search` still drives the instant client-side narrowing of the
  // rows already on screen (visibleContacts below).
  const debouncedSearch = useDebouncedValue(search, 300);

  // Parent-fund reference banner: searching a pod-shop parent (e.g.
  // "millennium") already matches its sub-funds' contacts server-side, so
  // list those sub-funds above the table to show what the results cover.
  const parentFundMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 3) return [];
    const allFirms = data?.firms ?? [];
    const childrenByParent = new Map();
    for (const f of allFirms) {
      const p = String(f.parent_firm_id ?? '').trim();
      if (!p) continue;
      if (!childrenByParent.has(p)) childrenByParent.set(p, []);
      childrenByParent.get(p).push(f.firm_name);
    }
    return allFirms
      .filter((f) => childrenByParent.has(f.firm_id) && String(f.firm_name ?? '').toLowerCase().includes(q))
      .slice(0, 3)
      .map((f) => ({
        firm_id: f.firm_id,
        name: f.firm_name,
        children: [...childrenByParent.get(f.firm_id)].sort((a, b) => String(a).localeCompare(String(b))),
      }));
  }, [search, data?.firms]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [selectedContactIds, setSelectedContactIds] = useState(() => new Set());
  const [selectedFirmIds, setSelectedFirmIds] = useState(() => new Set());
  const [deletingContacts, setDeletingContacts] = useState(false);
  const [deletingFirms, setDeletingFirms] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [backfillFirmStatus, setBackfillFirmStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [contactsColumns, setContactsColumns] = useState(() => new Set(DEFAULT_CONTACT_VISIBLE));
  const [firmsColumns, setFirmsColumns] = useState(() => {
    const s = new Set(FIRM_COLUMNS);
    s.delete('contact_count');
    return s;
  });
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filter, setFilter] = useState({ col: 'name', op: 'contains', val: '' });
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [expandedFirmIds, setExpandedFirmIds] = useState(() => new Set());
  const [firmContactsById, setFirmContactsById] = useState({});
  const [firmContactsLoading, setFirmContactsLoading] = useState({});
  const [firmContactsSearch, setFirmContactsSearch] = useState({});
  // Per-column filters for contacts table
  const [colFilters, setColFilters] = useState(() => {
    const pipelineDefault = (() => { try { const v = localStorage.getItem('contacts_pipeline_mode'); return v === null ? false : v === 'true'; } catch { return false; } })();
    return {
    // multiselect (arrays)
    _stage: pipelineDefault ? ['customer','trial','demo'] : [], _bd_high_intent: [], bd_status: [], sector_coverage: [], industry_focus: [], implied_sectors: [], sector_packs: [], job_function: [], _firm_owner: [],
    institution_type: [], side: [], asset_class: [], dominant_orientation: [], dominant_style: [],
    focus_orientation: [], focus_orientation_secondary: [], focus_industry_macro: [], focus_industry_mid: [],
    focus_industry_micro: [], focus_market_cap: [], focus_region: [], focus_strategy: [], focus_style: [],
    name: [], email: [], role: [], source: [], city: [], state_province: [], country_territory: [], _fund_type: [],
    // range (objects)
    relevance_score: { min: '', max: '', includeNull: false, onlyHasVal: false }, main_coverage: [],
    _firm_aum: { min: '', max: '', includeNull: false, onlyHasVal: false }, bd_aum_personal: { min: '', max: '', includeNull: false, onlyHasVal: false },
    account_aum: { min: '', max: '', includeNull: false, onlyHasVal: false }, _recent_touch_count: { min: '', max: '', includeNull: false, onlyHasVal: false },
  _touches: { min: '', max: '', includeNull: false, onlyHasVal: false },
    // date (objects)
    added_date: { after: '', before: '', nullMode: 'all' }, _last_touch_ts: { after: '', before: '', nullMode: 'all' }, _last_email_open_ts: { after: '', before: '', nullMode: 'all' },
  };
  });
  // Per-column filter modes for tag columns: 'any'|'all'|'none'|'only'  (default 'any')
  const [tagFilterModes, setTagFilterModes] = useState({});
  // Per-column filter modes for text columns: 'include'|'exclude'  (default 'include')
  const [textExcludeCols, setTextExcludeCols] = useState(() => new Set());

  function setTagMode(col, mode) {
    setTagFilterModes((prev) => ({ ...prev, [col]: mode }));
    setPage(1);
  }

  function toggleTextExclude(col) {
    setTextExcludeCols((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
    setPage(1);
  }

  // Per-column filters for firms table
  const [firmColFilters, setFirmColFilters] = useState({
    bd_status: [], fund_type: [], owner: [], firm_name: [], hq_city: [],
    aum: { min: '', max: '' }, score: { min: '', max: '' }, contact_count: { min: '', max: '' },
  });
  // Saving state for inline status overrides
  const [savingStatusId, setSavingStatusId] = useState(null);
  // Select-all-results (cross-page) state
  const [selectAllContactResults, setSelectAllContactResults] = useState(false);
  const [selectAllFirmResults, setSelectAllFirmResults] = useState(false);
  // Sync state — unified across all sheet↔BQ sync ops
  const [syncing, setSyncing] = useState({});         // { opKey: bool }
  const [syncResult, setSyncResult] = useState(null); // { ok, msg, conflicts }
  const [syncPanelOpen, setSyncPanelOpen] = useState(false);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [syncMenuOpen, setSyncMenuOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dedupOpen, setDedupOpen] = useState(false);
  const [moveFirmContact, setMoveFirmContact] = useState(null); // contact row for the move-firm modal
  const syncMenuWrapRef = useRef(null);
  // Email suggestions — infer missing emails from firm-mates' address pattern
  const [emailMapRows, setEmailMapRows] = useState(null); // /api/contacts/email-map rows, loaded lazily
  const emailMapLoadingRef = useRef(false);
  const [emailSuggestContact, setEmailSuggestContact] = useState(null); // row for the confirm dialog
  const [dismissedEmailSuggests, setDismissedEmailSuggests] = useState(() => new Set()); // contact_ids, session-local

  // Pipeline view mode
  const [pipelineMode, setPipelineMode] = useState(() => {
    try { const v = localStorage.getItem('contacts_pipeline_mode'); return v === null ? false : v === 'true'; } catch { return false; }
  });

  // Export dropdown open state
  const [exportOpen, setExportOpen] = useState(false);
  const exportBtnRef = useRef(null);
  const exportWrapRef = useRef(null);
  const [bigDoughModalOpen, setBigDoughModalOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [batchSplitOpen, setBatchSplitOpen] = useState(false);

  const TAG_COLS = new Set(['sector_coverage','industry_focus','implied_sectors','sector_packs','job_function','institution_type','side','asset_class',
    'dominant_orientation','dominant_style','focus_orientation','focus_orientation_secondary',
    'focus_industry_macro','focus_industry_mid','focus_industry_micro','focus_market_cap',
    'focus_region','focus_strategy','focus_style']);

  function buildFilters() {
    const result = [...appliedFilters];
    for (const [col, vals] of Object.entries(colFilters)) {
      if (Array.isArray(vals)) {
        if (vals.length === 0) continue;
        if (col === '_bd_high_intent') {
          // Yes/No goes through as the boolean; each signal value becomes its
          // own count filter (AND-ed with the rest).
          const tf = vals.filter((v) => v === 'true' || v === 'false');
          if (tf.length > 0) result.push({ col, op: 'in', val: tf.join('\n') });
          for (const v of vals) {
            if (BD_SIGNAL_FILTERS[v]) result.push({ ...BD_SIGNAL_FILTERS[v] });
          }
        } else if (TAG_COLS.has(col)) {
          const mode = tagFilterModes[col] ?? 'any';
          const opMap = { any: 'contains_any', all: 'contains_all', within: 'tag_within', none: 'contains_none', only: 'tag_exact' };
          result.push({ col, op: opMap[mode] ?? 'contains_any', val: vals.join('\n') });
        } else {
          const exclude = textExcludeCols.has(col);
          result.push({ col, op: exclude ? 'not_in' : 'in', val: vals.join('\n') });
        }
      } else if (vals && typeof vals === 'object') {
        if ('min' in vals) {
          // Touches timeframe: a marker op the backend reads to re-window
          // _recent_touch_count (it adds no WHERE condition). Sent even with
          // no min/max so the displayed column reflects the chosen window.
          if (col === '_recent_touch_count' && vals.window && vals.window !== DEFAULT_TOUCH_WINDOW) {
            result.push({ col, op: 'window', val: String(vals.window) });
          }
          if (vals.onlyHasVal) {
            result.push({ col, op: 'not_empty', val: '' });
          } else {
            const nullOp = vals.includeNull;
            // AUM columns store $M; the filter UI's unit toggle defaults to $B.
            const scale = AUM_UNIT_COLS.has(col) && (vals.unit ?? 'B') === 'B' ? 1000 : 1;
            if (vals.min !== '') result.push({ col, op: nullOp ? 'gte_or_null' : 'gte', val: String(parseFloat(vals.min) * scale) });
            if (vals.max !== '') result.push({ col, op: nullOp ? 'lte_or_null' : 'lte', val: String(parseFloat(vals.max) * scale) });
          }
        } else if ('after' in vals) {
          const nm = vals.nullMode ?? 'all';
          if (nm === 'no_date')  { result.push({ col, op: 'is_null',   val: '' }); }
          else if (nm === 'has_date') { result.push({ col, op: 'not_empty', val: '' }); }
          else {
            const orNull = nm === 'include_no_date';
            if (vals.after)  result.push({ col, op: orNull ? 'after_or_null'  : 'after',  val: vals.after });
            if (vals.before) result.push({ col, op: orNull ? 'before_or_null' : 'before', val: vals.before });
          }
        }
      }
    }
    return result;
  }

  function buildFirmFilters() {
    const result = [...appliedFilters];
    for (const [col, vals] of Object.entries(firmColFilters)) {
      if (Array.isArray(vals)) {
        if (vals.length === 0) continue;
        result.push({ col, op: 'in', val: vals.join('\n') });
      } else if (vals && typeof vals === 'object' && 'min' in vals) {
        // aum filter is entered in B but stored in M — multiply by 1000
        const scale = col === 'aum' ? 1000 : 1;
        if (vals.min !== '') result.push({ col, op: 'gte', val: String(parseFloat(vals.min) * scale) });
        if (vals.max !== '') result.push({ col, op: 'lte', val: String(parseFloat(vals.max) * scale) });
      }
    }
    return result;
  }

  const CONTACT_COL_ALIAS = { fund_type: '_fund_type', firm_name: '_firm_name' };

  const INITIAL_FIRM_COL_FILTERS = {
    bd_status: [], fund_type: [], owner: [], firm_name: [], hq_city: [],
    aum: { min: '', max: '' }, score: { min: '', max: '' }, contact_count: { min: '', max: '' },
  };

  function isFilterActive(vals) {
    if (Array.isArray(vals)) return vals.length > 0;
    if (vals && typeof vals === 'object') {
      if ('nullMode' in vals) return (vals.nullMode !== 'all' && vals.nullMode != null) || !!vals.after || !!vals.before;
      if ('min' in vals) return !!vals.includeNull || !!vals.onlyHasVal || (vals.min !== '' && vals.min != null) || (vals.max !== '' && vals.max != null);
      return Object.values(vals).some((v) => v && String(v).trim());
    }
    return false;
  }

  // Human-readable one-liner per active contact filter ("Stage: Warm, Cold",
  // "Firm AUM: ≥ 1$B") — shown as chips under the Breakdown grid's title so a
  // screenshot/copy of the grid carries the context it was computed under.
  function describeActiveFilters() {
    const parts = [];
    const q = search.trim();
    if (q) parts.push(`Search: “${q}”`);
    for (const [col, vals] of Object.entries(colFilters)) {
      if (!isFilterActive(vals)) continue;
      const label = CONTACT_LABELS[col] ?? col;
      if (Array.isArray(vals)) {
        const shown = col === '_stage' ? vals.map((v) => STAGE_LABELS[v] ?? v)
          : col === '_bd_high_intent' ? vals.map((v) => (v === 'true' ? 'Yes' : v === 'false' ? 'No' : BD_SIGNAL_LABELS[v] ?? v))
          : vals;
        if (TAG_COLS.has(col)) {
          const mode = tagFilterModes[col] ?? 'any';
          const prefix = { any: '', all: 'all of ', none: 'none of ', only: 'exactly ', within: 'within ' }[mode] ?? '';
          parts.push(`${label}: ${prefix}${shown.join(', ')}`);
        } else {
          parts.push(`${label}${textExcludeCols.has(col) ? ' not' : ''}: ${shown.join(', ')}`);
        }
      } else if ('min' in vals) {
        const unit = AUM_UNIT_COLS.has(col) ? `$${vals.unit ?? 'B'}` : '';
        const name = col === '_recent_touch_count' ? `Touches (${touchWindowShort(vals.window)})` : label;
        const bits = [];
        if (vals.min !== '') bits.push(`≥ ${vals.min}${unit}`);
        if (vals.max !== '') bits.push(`≤ ${vals.max}${unit}`);
        if (vals.onlyHasVal) bits.push('has value');
        if (vals.includeNull) bits.push('or blank');
        if (bits.length) parts.push(`${name}: ${bits.join(', ')}`);
      } else if ('after' in vals) {
        const bits = [];
        if (vals.nullMode === 'no_date') bits.push('no date');
        else if (vals.nullMode === 'has_date') bits.push('has date');
        else {
          if (vals.after) bits.push(`after ${vals.after}`);
          if (vals.before) bits.push(`before ${vals.before}`);
          if (vals.nullMode === 'include_no_date') bits.push('or no date');
        }
        if (bits.length) parts.push(`${label}: ${bits.join(', ')}`);
      }
    }
    for (const f of appliedFilters) {
      parts.push(`${CONTACT_LABELS[f.col] ?? f.col} ${f.op} ${f.val}`.trim());
    }
    return parts;
  }

  const hasActiveContactFilters = appliedFilters.length > 0 || Object.values(colFilters).some(isFilterActive);
  const hasActiveFirmFilters = appliedFilters.length > 0 || Object.values(firmColFilters).some(isFilterActive);

  // Debounced filter payloads: checking several filter boxes in quick
  // succession fires one fetch with the final set instead of one per click
  // (each click previously cost a full backend query; stale responses were
  // discarded but the work was still done). Serialized so the effect can
  // dep-compare by value.
  const debouncedContactFiltersJson = useDebouncedValue(JSON.stringify(buildFilters()), 350);
  const debouncedFirmFiltersJson    = useDebouncedValue(JSON.stringify(buildFirmFilters()), 350);
  // A just-clicked filter hasn't hit the debounced value yet — the spinner
  // covers that window too so the click gives instant feedback.
  const filtersPending = view === 'contacts'
    ? JSON.stringify(buildFilters()) !== debouncedContactFiltersJson
    : JSON.stringify(buildFirmFilters()) !== debouncedFirmFiltersJson;

  function countActiveFilters(filterMap) {
    return Object.values(filterMap).reduce((sum, v) => sum + (isFilterActive(v) ? 1 : 0), 0);
  }
  const totalActiveContactFilters = countActiveFilters(colFilters) + appliedFilters.length;
  const totalActiveFirmFilters = countActiveFilters(firmColFilters) + appliedFilters.length;

  // ── Saved-views: canonical pipeline state ────────────────────────────────────
  const PIPELINE_CF = { ...INITIAL_COL_FILTERS, _stage: ['customer', 'trial', 'demo'] };

  // Dirty: current state differs from active view's saved snapshot
  const viewDirty = useMemo(() => {
    const curCols = [...contactsColumns].sort();
    const cur = canonState(colFilters, appliedFilters, sortKey, sortDir, curCols, tagFilterModes, textExcludeCols);
    if (activeViewId === PIPELINE_VIEW_ID) {
      const target = canonState(PIPELINE_CF, [], null, 'asc', [...DEFAULT_CONTACT_VISIBLE].sort(), {}, new Set());
      return cur !== target;
    }
    const sv = savedViews.find((v) => v.id === activeViewId);
    if (!sv) return false;
    const tCols = [...(sv.columns ?? DEFAULT_CONTACT_VISIBLE)].sort();
    const savedCF = { ...INITIAL_COL_FILTERS, ...(sv.filters?.colFilters ?? {}) };
    const savedTM = sv.filters?.tagFilterModes ?? {};
    const savedTE = new Set(sv.filters?.textExcludeCols ?? []);
    const target = canonState(savedCF, sv.filters?.appliedFilters ?? [], sv.sort?.sortKey ?? null, sv.sort?.sortDir ?? 'asc', tCols, savedTM, savedTE);
    return cur !== target;
  }, [colFilters, appliedFilters, sortKey, sortDir, contactsColumns, activeViewId, savedViews, tagFilterModes, textExcludeCols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load views once on mount
  useEffect(() => {
    fetchContactViews(getToken).then(setSavedViews).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-toast: 2+ active filters, dirty, not dismissed, contacts view
  useEffect(() => {
    if (view !== 'contacts' || autoToastDismissed) return;
    if (totalActiveContactFilters >= 2 && viewDirty) {
      const t = setTimeout(() => setAutoToast(true), 1800);
      return () => clearTimeout(t);
    }
    setAutoToast(false);
  }, [totalActiveContactFilters, viewDirty, view, autoToastDismissed]);

  function applyView(sv) {
    if (sv.id === PIPELINE_VIEW_ID) {
      setPipelineMode(true);
      setColFilters({ ...INITIAL_COL_FILTERS, _stage: ['customer', 'trial', 'demo'] });
      setAppliedFilters([]);
      setSortKey(null);
      setSortDir('asc');
      setContactsColumns(new Set(DEFAULT_CONTACT_VISIBLE));
      setTagFilterModes({});
      setTextExcludeCols(new Set());
    } else {
      setPipelineMode(false);
      // Merge with INITIAL_COL_FILTERS so older saved views missing newer keys don't break the filter panel
      setColFilters({ ...INITIAL_COL_FILTERS, ...(sv.filters?.colFilters ?? {}) });
      setAppliedFilters(sv.filters?.appliedFilters ?? []);
      setSortKey(sv.sort?.sortKey ?? null);
      setSortDir(sv.sort?.sortDir ?? 'asc');
      setContactsColumns(new Set(sv.columns ?? DEFAULT_CONTACT_VISIBLE));
      setTagFilterModes(sv.filters?.tagFilterModes ?? {});
      setTextExcludeCols(new Set(sv.filters?.textExcludeCols ?? []));
    }
    setActiveViewId(sv.id);
    setAutoToast(false);
    setAutoToastDismissed(false);
    setPage(1);
  }

  function flashViewMsg(ok, text) {
    setViewSaveMsg({ ok, text });
    setTimeout(() => setViewSaveMsg(null), 2200);
  }

  async function handleSaveNewView(name) {
    setSavingView(true);
    try {
      const payload = {
        name,
        filters: { colFilters, appliedFilters, tagFilterModes, textExcludeCols: [...textExcludeCols] },
        sort: { sortKey, sortDir },
        columns: [...contactsColumns],
      };
      const created = await createContactView(getToken, payload);
      setSavedViews((prev) => [...prev, created]);
      setActiveViewId(created.id);
      setSaveModal(false);
      setAutoToast(false);
      setAutoToastDismissed(false);
      flashViewMsg(true, `"${name}" saved`);
    } catch (e) {
      console.error('[save view]', e);
      flashViewMsg(false, 'Save failed');
    } finally {
      setSavingView(false);
    }
  }

  async function handleUpdateCurrentView() {
    if (activeViewId === PIPELINE_VIEW_ID) return;
    setSavingView(true);
    const viewName = savedViews.find((v) => v.id === activeViewId)?.name ?? 'View';
    try {
      const payload = {
        filters: { colFilters, appliedFilters, tagFilterModes, textExcludeCols: [...textExcludeCols] },
        sort: { sortKey, sortDir },
        columns: [...contactsColumns],
      };
      await updateContactView(getToken, activeViewId, payload);
      setSavedViews((prev) => prev.map((v) => v.id === activeViewId ? { ...v, ...payload } : v));
      flashViewMsg(true, `"${viewName}" updated`);
    } catch (e) {
      console.error('[update view]', e);
      flashViewMsg(false, 'Update failed');
    } finally {
      setSavingView(false);
    }
  }

  async function handleRenameView(id, newName) {
    try {
      await updateContactView(getToken, id, { name: newName });
      setSavedViews((prev) => prev.map((v) => v.id === id ? { ...v, name: newName } : v));
    } catch (e) {
      console.error('[rename view]', e);
    }
  }

  async function handleDeleteView(id) {
    try {
      await deleteContactView(getToken, id);
      setSavedViews((prev) => prev.filter((v) => v.id !== id));
      if (activeViewId === id) applyView({ id: PIPELINE_VIEW_ID });
    } catch (e) {
      console.error('[delete view]', e);
    }
  }
  // ─────────────────────────────────────────────────────────────────────────────

  function clearAllContactFilters() {
    setColFilters(INITIAL_COL_FILTERS);
    setAppliedFilters([]);
    setTagFilterModes({});
    setTextExcludeCols(new Set());
    setSelectAllContactResults(false);
    setSelectedContactIds(new Set());
    setPage(1);
  }

  function clearAllFirmFilters() {
    setFirmColFilters(INITIAL_FIRM_COL_FILTERS);
    setAppliedFilters([]);
    setSelectAllFirmResults(false);
    setSelectedFirmIds(new Set());
    setPage(1);
  }

  async function fetchAllContactRows() {
    const res = await fetchContactsPage(getToken, {
      page: 1, limit: 99999, sort: sortKey, dir: sortDir, q: search, filters: buildFilters(),
    });
    return res.rows ?? [];
  }

  async function fetchAllFirmRows() {
    const res = await fetchFirmsPage(getToken, {
      page: 1, limit: 99999, sort: sortKey, dir: sortDir, q: search, filters: buildFirmFilters(),
    });
    return res.rows ?? res ?? [];
  }

  // Download the current filtered view (all pages, server-filtered) as CSV.
  // Every column the API returns is included — the full DB row plus the
  // derived fields (_stage, _firm_name, _firm_aum, ...), not just what the
  // table displays.
  async function handleExportCsv() {
    setExporting(true);
    try {
      // DATE/TIMESTAMP cells arrive as { value } objects — unwrap for the CSV.
      const cell = (x) => {
        let v = (x && typeof x === 'object' && 'value' in x) ? x.value : x;
        if (v && typeof v === 'object') v = JSON.stringify(v);
        return `"${String(v ?? '').replace(/"/g, '""')}"`;
      };
      const rows = view === 'contacts' ? await fetchAllContactRows() : await fetchAllFirmRows();
      let cols;
      if (view === 'contacts') {
        // Fixed column set/order per Lilly's export spec.
        cols = [
          ['Name', 'name'], ['Email', 'email'], ['Firm', '_firm_name'],
          ['Coverage', 'sector_coverage'], ['Industry Focus', 'industry_focus'],
          ['Main Coverage', 'main_coverage'], ['Focus Region', 'focus_region'],
          ['Institution Type', 'institution_type'], ['Focus Strategy', 'focus_strategy'],
          ['Firm AUM', '_firm_aum'], ['Last Touch', '_last_touch_ts'],
          ['Touches', '_touches'], ['Last Email Open', '_last_email_open_ts'],
          [`Touches (${touchWindowShort(colFilters._recent_touch_count?.window)})`, '_recent_touch_count'], ['Stage', '_stage'],
          ['High Intent', '_bd_high_intent'],
          ['Status', 'bd_status'], ['Fund Type', '_fund_type'],
        ];
      } else {
        cols = [
          ['Firm', 'firm_name'], ['Fund Type', 'fund_type'], ['Status', 'bd_status'],
          ['Owner', 'owner'], ['AUM', 'aum'], ['HQ City', 'hq_city'], ['Contacts', 'contact_count'],
        ];
      }
      const csv = [
        cols.map(([h]) => h).join(','),
        // *_ts columns are ISO timestamps — trim to the date for readability.
        ...rows.map((r) => cols.map(([, k]) =>
          cell(k.endsWith('_ts') && r[k] ? String(r[k]).slice(0, 10)
            : k === '_bd_high_intent' ? (r[k] ? 'Yes' : '')
            : r[k])).join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `${view}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  async function handleContactStatusSave(contactId, newStatus) {
    setSavingStatusId(contactId);
    try {
      await updateContact(getToken, contactId, { bd_status: newStatus });
      setContacts((prev) => prev.map((c) => c.contact_id === contactId ? { ...c, bd_status: newStatus } : c));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('[handleContactStatusSave]', err);
      alert(`Failed to update status: ${err.message}`);
    } finally {
      setSavingStatusId(null);
    }
  }

  async function handleFirmStatusSave(firmId, newStatus) {
    setSavingStatusId(firmId);
    try {
      await updateFirm(getToken, firmId, { bd_status: newStatus });
      setFirms((prev) => prev.map((f) => f.firm_id === firmId ? { ...f, bd_status: newStatus } : f));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('[handleFirmStatusSave]', err);
      alert(`Failed to update status: ${err.message}`);
    } finally {
      setSavingStatusId(null);
    }
  }

  async function handleSync(key, apiFn, label) {
    setSyncing((s) => ({ ...s, [key]: true }));
    setSyncResult(null);
    setConflictsOpen(false);
    try {
      const result = await apiFn(getToken);
      const inserted  = result.inserted ?? 0;
      const appended  = result.appended ?? 0;
      const updated   = result.updated  ?? 0;
      const skipped      = result.skipped_existing ?? 0;
      const conflicts    = result.conflicts ?? [];
      const newCols      = result.new_columns ?? [];
      const fieldsUpdated = result.fields_updated ?? [];
      const parts = [];
      if (inserted > 0)  parts.push(`${inserted} inserted`);
      if (updated > 0)   parts.push(`${updated} updated${fieldsUpdated.length ? ` (${fieldsUpdated.join(', ')})` : ''}`);
      if (appended > 0)  parts.push(`${appended} appended`);
      if (skipped > 0)   parts.push(`${skipped.toLocaleString()} already in BQ`);
      if (parts.length === 0) parts.push('0 rows changed');
      if (newCols.length > 0) parts.push(`${newCols.length} new column${newCols.length === 1 ? '' : 's'}`);
      const conflictExtra = result.conflict_count > 0 ? `, ${result.conflict_count} field conflict${result.conflict_count === 1 ? '' : 's'}` : '';
      setSyncResult({ ok: true, msg: `${label}: ${parts.join(', ')}${conflictExtra}`, conflicts });
      if (conflicts.length > 0) setConflictsOpen(true);
      setFirms([]); setContacts([]); setPage(1); setRefreshKey((k) => k + 1);
    } catch (e) {
      setSyncResult({ ok: false, msg: e.message, conflicts: [] });
    } finally {
      setSyncing((s) => ({ ...s, [key]: false }));
    }
  }

  const isSyncing = Object.values(syncing).some(Boolean);

  function togglePipelineMode(val) {
    setPipelineMode(val);
    try { localStorage.setItem('contacts_pipeline_mode', String(val)); } catch {}
    if (val) {
      setColFilters((prev) => ({ ...prev, _stage: ['customer', 'trial', 'demo'] }));
    } else {
      setColFilters((prev) => ({ ...prev, _stage: [] }));
    }
    setPage(1);
  }

  const pipelineStats = useMemo(() => {
    if (!pipelineMode || view !== 'contacts') return null;
    const weights = {
      customer:        1.0,
      trial:           Number(settings.stageWeightTrial      ?? STAGE_WEIGHTS.trial),
      demo:            Number(settings.stageWeightDemo       ?? STAGE_WEIGHTS.demo),
      warm:            Number(settings.stageWeightWarm       ?? STAGE_WEIGHTS.warm),
      cold:            Number(settings.stageWeightCold       ?? STAGE_WEIGHTS.cold),
      'cold-untouched':Number(settings.stageWeightUntouched  ?? STAGE_WEIGHTS['cold-untouched']),
      dead:            0,
    };
    let weightedArr = 0;
    const stageCounts = {};
    for (const c of contacts) {
      const stage = c._stage;
      if (stage) stageCounts[stage] = (stageCounts[stage] ?? 0) + 1;
      const aum = parseFloat(c._firm_aum) || 0;
      weightedArr += aum * 1_000_000 * (weights[stage] ?? 0);
    }
    return { weightedArr, stageCounts };
  }, [pipelineMode, view, contacts, settings]);

  useEffect(() => {
    if (!exportOpen) return;
    function onDown(e) { if (exportWrapRef.current && !exportWrapRef.current.contains(e.target)) setExportOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [exportOpen]);

  useEffect(() => {
    if (!syncMenuOpen) return;
    function onDown(e) { if (syncMenuWrapRef.current && !syncMenuWrapRef.current.contains(e.target)) setSyncMenuOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [syncMenuOpen]);

  useEffect(() => {
    if (!data) return;
    setContacts([]);
    setFirms([]);
  }, [data]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        if (view === 'contacts') {
          const res = await fetchContactsPage(getToken, {
            page,
            limit: PAGE_SIZE,
            sort: sortKey ?? '',
            dir: sortDir,
            q: debouncedSearch,
            filters: JSON.parse(debouncedContactFiltersJson),
          });
          if (!alive) return;
          setContacts(normalizeRows(res.rows ?? []));
          setContactsTotal(Number(res.total ?? 0));
        } else {
          const res = await fetchFirmsPage(getToken, {
            page,
            limit: PAGE_SIZE,
            sort: sortKey ?? '',
            dir: sortDir,
            q: debouncedSearch,
            filters: JSON.parse(debouncedFirmFiltersJson),
          });
          if (!alive) return;
          setFirms(res.rows ?? res ?? []);
          setFirmsTotal(Number(res.total ?? 0));
        }
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [getToken, view, page, sortKey, sortDir, debouncedSearch, debouncedContactFiltersJson, debouncedFirmFiltersJson, data, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimistic: apply a contact save (from the App-level edit/create panel) to
  // the rows already on screen the moment the panel closes — the server
  // refetch above confirms with fresh data a few seconds later.
  useEffect(() => {
    const c = savedContactPing?.contact;
    if (!c?.contact_id) return;
    const firmName = (data?.firms ?? []).find((f) => f.firm_id === c.firm_id)?.firm_name;
    setContacts((prev) => prev.map((r) => r.contact_id === c.contact_id
      ? { ...r, ...c, ...(firmName !== undefined ? { _firm_name: firmName } : {}) }
      : r));
  }, [savedContactPing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Email suggestions: once the visible page has a contact with no email but a
  // firm, pull the full email map (one cheap cached call) so firm-mates'
  // addresses — on this page or not — can supply the firm's pattern.
  useEffect(() => {
    if (view !== 'contacts' || emailMapRows || emailMapLoadingRef.current) return;
    if (!contacts.some((r) => r.contact_id && !r.email && r.firm_id)) return;
    emailMapLoadingRef.current = true;
    fetchContactEmailMap(getToken)
      .then((res) => setEmailMapRows(res.rows ?? []))
      .catch((e) => { console.error('[email suggestions]', e); emailMapLoadingRef.current = false; });
  }, [view, contacts, emailMapRows, getToken]);

  const firmEmailPatterns = useMemo(
    () => (emailMapRows ? buildFirmPatterns(emailMapRows) : null),
    [emailMapRows],
  );
  // email (lowercased) → holder's name, for the collision guard + its message.
  const emailHolders = useMemo(() => {
    const m = new Map();
    for (const r of emailMapRows ?? []) {
      const e = String(r.email ?? '').trim().toLowerCase();
      if (e && !m.has(e)) m.set(e, r.name);
    }
    return m;
  }, [emailMapRows]);

  // Suggested email for a visible row, or null (has one already / no firm
  // pattern / dismissed / no non-colliding candidate). When the firm's
  // pattern yields an address someone else already has (two Aarons), the
  // suggestion falls back to a disambiguated shape and carries `conflict`
  // so the dialog can say whose address blocked the obvious guess.
  function getEmailSuggestion(row) {
    if (!firmEmailPatterns || !row.contact_id || row.email || !row.firm_id) return null;
    if (dismissedEmailSuggests.has(row.contact_id)) return null;
    const firmPattern = firmEmailPatterns.get(row.firm_id);
    if (!firmPattern) return null;
    const s = suggestEmailAvoidingTaken(row.name, firmPattern, emailHolders);
    if (!s) return null;
    return {
      email: s.email,
      conflict: s.fallback ? { email: s.conflictEmail, name: emailHolders.get(s.conflictEmail) } : null,
      ...firmPattern,
    };
  }

  async function handleEmailSuggestSave(row, email) {
    await updateContact(getToken, row.contact_id, { email });
    setContacts((prev) => prev.map((c) => c.contact_id === row.contact_id ? { ...c, email } : c));
    // Feed the save back into the pattern pool so sibling suggestions see it.
    setEmailMapRows((prev) => prev
      ? [...prev, { contact_id: row.contact_id, name: row.name, email, firm_id: row.firm_id }]
      : prev);
    setEmailSuggestContact(null);
  }

  // Step 1: when a highlight request arrives, switch to contacts view and filter to that contact's name
  useEffect(() => {
    if (!highlightContact) return;
    setView('contacts');
    setPage(1);
    setSearch('');
    // Filter by exact name so the contact appears on the first page
    if (highlightContact.name) {
      setColFilters((prev) => ({ ...prev, name: [highlightContact.name] }));
    }
    // Consume the prop immediately so it never fires again
    onClearHighlight?.();
    // Store the id locally to drive the scroll + flash once data loads
    setFlashContactId(highlightContact.contact_id ?? null);
  }, [highlightContact]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: once contacts data has loaded and the target row is rendered, scroll and flash
  useEffect(() => {
    if (!flashContactId || loading) return;
    const found = contacts.find((r) => r.contact_id === flashContactId);
    if (!found) return;
    requestAnimationFrame(() => {
      if (highlightRowRef.current) {
        highlightRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Clear flash after animation duration
      setTimeout(() => setFlashContactId(null), 2500);
    });
  }, [flashContactId, loading, contacts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set indeterminate state on header checkboxes
  useEffect(() => {
    if (!contactHeaderCheckRef.current) return;
    const allChecked = selectAllContactResults || (rows.length > 0 && rows.every((c) => c.contact_id && selectedContactIds.has(c.contact_id)));
    const someChecked = !selectAllContactResults && rows.some((c) => c.contact_id && selectedContactIds.has(c.contact_id));
    contactHeaderCheckRef.current.indeterminate = !allChecked && someChecked;
  });

  useEffect(() => {
    if (!firmHeaderCheckRef.current) return;
    const allChecked = selectAllFirmResults || (rows.length > 0 && rows.every((r) => r.firm_id && selectedFirmIds.has(r.firm_id)));
    const someChecked = !selectAllFirmResults && rows.some((r) => r.firm_id && selectedFirmIds.has(r.firm_id));
    firmHeaderCheckRef.current.indeterminate = !allChecked && someChecked;
  });

  const visibleContacts = useMemo(() => contacts.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(q));
  }), [contacts, search]);

  const displayColumns = useMemo(() => CONTACT_COLUMNS, []);

  function handleStageOverride(contactId, newOverride) {
    const updater = (c) =>
      c.contact_id === contactId
        ? { ...c, stage_override: newOverride, _stage: newOverride || c._stage }
        : c;
    setContacts((prev) => prev.map(updater));
    setFirmContactsById((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = next[key].map(updater);
      }
      return next;
    });
    if (newOverride === 'trial') {
      const existingTrials = data?.trials ?? [];
      const hasActiveTrial = existingTrials.some(
        (t) => t.contact_id === contactId && String(t.status ?? '').toLowerCase() === 'active'
      );
      console.log('[handleStageOverride] trial path', { contactId, hasActiveTrial, trialsCount: existingTrials.length });
      if (!hasActiveTrial) {
        const contact = contacts.find((c) => c.contact_id === contactId);
        const today = new Date().toLocaleDateString('sv');
        const trialDays = Number(settings?.trialDefaultDays ?? 30);
        const endDate = new Date(new Date(today).getTime() + trialDays * 86_400_000).toISOString().slice(0, 10);
        const trialId = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        console.log('[handleStageOverride] calling createTrial', { trialId, contactId, firmId: contact?.firm_id, today, endDate });
        createTrial(getToken, {
          trial_id: trialId,
          contact_id: contactId,
          firm_id: contact?.firm_id ?? '',
          start_date: today,
          end_date: endDate,
          status: 'Active',
        }).then(() => {
          console.log('[handleStageOverride] createTrial success');
          setTrialCreatedToast({ endDate });
          setTimeout(() => setTrialCreatedToast(null), 6000);
          onRefresh?.();
        }).catch((e) => {
          console.error('Auto-create trial failed:', e);
          setTrialCreatedToast({ error: e.message });
          setTimeout(() => setTrialCreatedToast(null), 8000);
        });
      } else {
        console.log('[handleStageOverride] skipped — active trial already exists');
      }
    }
  }

  async function handleDeleteContacts(ids) {
    setDeletingContacts(true);
    try {
      // Offboard (soft delete), not hard delete — hides the person everywhere,
      // removes their trials, locks their account, and survives BD re-imports.
      await Promise.all(ids.map((id) => offboardContact(getToken, id)));
      invalidateSheetCache();
      onRefresh?.();
    } finally {
      setDeletingContacts(false);
    }
  }

  async function handleDeleteFirms(ids) {
    setDeletingFirms(true);
    try {
      await Promise.all(ids.map((id) => deleteFirm(getToken, id)));
      invalidateSheetCache();
      onRefresh?.();
    } finally {
      setDeletingFirms(false);
    }
  }

  function openDeleteConfirm(kind, ids, impact) {
    setConfirmModal({ kind, ids, impact });
  }

  async function confirmDelete() {
    if (!confirmModal) return;
    const { kind, ids } = confirmModal;
    setConfirmModal(null);
    if (kind === 'contacts') {
      await handleDeleteContacts(ids);
      setSelectedContactIds(new Set());
    } else {
      await handleDeleteFirms(ids);
      setSelectedFirmIds(new Set());
    }
  }

  function toggleSelected(setter, id) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function clearSelectedCurrentView() {
    if (view === 'contacts') setSelectedContactIds(new Set());
    else setSelectedFirmIds(new Set());
  }

  function selectAllOnPage() {
    if (view === 'contacts') {
      setSelectedContactIds((prev) => {
        const next = new Set(prev);
        rows.forEach((c) => c.contact_id && next.add(c.contact_id));
        return next;
      });
    } else {
      setSelectedFirmIds((prev) => {
        const next = new Set(prev);
        rows.forEach((r) => r.firm_id && next.add(r.firm_id));
        return next;
      });
    }
  }

  async function handleExportAll() {
    setExportOpen(false);
    const date = new Date().toISOString().slice(0, 10);
    if (view === 'contacts') {
      const allRows = selectAllContactResults ? await fetchAllContactRows() : rows.filter((r) => r.contact_id && selectedContactIds.has(r.contact_id));
      downloadCsv(`contacts-${date}.csv`, allRows);
    } else {
      const allRows = selectAllFirmResults ? await fetchAllFirmRows() : rows.filter((r) => r.firm_id && selectedFirmIds.has(r.firm_id));
      downloadCsv(`firms-${date}.csv`, allRows);
    }
  }

  async function handleExportForGmail() {
    setExportOpen(false);
    const date = new Date().toISOString().slice(0, 10);
    const allRows = selectAllContactResults ? await fetchAllContactRows() : rows.filter((r) => r.contact_id && selectedContactIds.has(r.contact_id));
    const gmailRows = allRows.map((r) => {
      const name = String(r.name ?? '').trim();
      const spaceIdx = name.indexOf(' ');
      const first_name = spaceIdx >= 0 ? name.slice(0, spaceIdx) : name;
      const last_name = spaceIdx >= 0 ? name.slice(spaceIdx + 1) : '';
      return { first_name, last_name, email: r.email ?? '' };
    });
    downloadCsv(`contacts-gmail-${date}.csv`, gmailRows);
  }

  async function handleExportForBigDough(selectedCols) {
    setBigDoughModalOpen(false);
    const date = new Date().toISOString().slice(0, 10);
    const allRows = selectAllContactResults ? await fetchAllContactRows() : rows.filter((r) => r.contact_id && selectedContactIds.has(r.contact_id));
    const bdRows = allRows.map((r) => {
      const out = { Approved: r.bd_status?.toLowerCase() === 'approved' ? 'Y' : 'N' };
      for (const col of selectedCols) {
        const label = BIGDOUGH_COL_LABELS[col] ?? col;
        // firm_name is stored as _firm_name in joined contact rows
        const val = col === 'firm_name' ? (r._firm_name ?? r.firm_name ?? '') : (r[col] ?? '');
        out[label] = val;
      }
      return out;
    });
    downloadCsv(`contacts-bigdough-${date}.csv`, bdRows);
  }

  function toggleColumn(col) {
    const set = view === 'contacts' ? new Set(contactsColumns) : new Set(firmsColumns);
    if (set.has(col)) set.delete(col); else set.add(col);
    if (view === 'contacts') setContactsColumns(set); else setFirmsColumns(set);
  }

  function toggleAllColumns() {
    if (view === 'contacts') setContactsColumns(new Set(DEFAULT_CONTACT_VISIBLE));
    else setFirmsColumns(new Set(FIRM_COLUMNS));
  }

  function handleSort(key) {
    const mappedKey = CONTACT_COL_ALIAS[key] ?? key;
    if (sortKey === mappedKey) {
      if (sortDir === 'asc') { setSortDir('desc'); }
      else { setSortKey(null); setSortDir('asc'); }
    } else { setSortKey(mappedKey); setSortDir('asc'); }
    setPage(1);
  }

  function applyFilter() {
    if (!filter.col || !filter.op) return;
    setAppliedFilters((prev) => [...prev.filter((f) => f.col !== filter.col), { ...filter }]);
    setPage(1);
    setFilterOpen(false);
  }

  function clearFilters() {
    setAppliedFilters([]);
    setFilter({ col: 'name', op: 'contains', val: '' });
    setPage(1);
  }

  function renderFirmValue(row, col) {
    if (col === 'bd_status')     return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        <StatusPicker
          value={row.bd_status ?? ''}
          saving={savingStatusId === row.firm_id}
          onSave={(s) => handleFirmStatusSave(row.firm_id, s)}
        />
        {row.parent_firm_name && (
          <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--c-text-3)', background: 'var(--c-bg-2)', border: '1px solid var(--c-border)', borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap' }}>
            ↑ {row.parent_firm_name}
          </span>
        )}
      </span>
    );
    if (col === 'firm_name')     return <div style={{ fontWeight: 700 }}>{row.firm_name ?? '—'}</div>;
    if (col === 'aum')           return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {formatAumBn(row.aum)}
        {row.parent_firm_name && (
          <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--c-text-3)', background: 'var(--c-bg-2)', border: '1px solid var(--c-border)', borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap' }}>
            ↑ {row.parent_firm_name}
          </span>
        )}
      </span>
    );
    if (col === 'score')         return row.score != null && row.score !== '' ? (
      <span style={{ fontWeight: 600, color: Number(row.score) > 0 ? 'var(--c-primary)' : 'var(--c-text-3)' }}>
        {Number(row.score).toFixed(1)}
      </span>
    ) : <span style={{ color: 'var(--c-text-3)' }}>—</span>;
    if (col === 'contact_count') return String(row.contact_count ?? 0);
    if (col === 'main_coverage_count') return String(row.main_coverage_count ?? 0);
    if (col === '_stage_counts') return <StageCounts counts={row._stage_counts} />;
    return fmt(row[col]);
  }

  async function toggleFirmExpanded(firmKey, firmName) {
    const next = new Set(expandedFirmIds);
    if (next.has(firmKey)) {
      next.delete(firmKey);
      setExpandedFirmIds(next);
      return;
    }
    next.add(firmKey);
    setExpandedFirmIds(next);
    if (firmContactsById[firmKey] || firmContactsLoading[firmKey]) return;
    setFirmContactsLoading((prev) => ({ ...prev, [firmKey]: true }));
    try {
      const res = await fetchFirmContacts(getToken, firmName ?? firmKey);
      setFirmContactsById((prev) => ({ ...prev, [firmKey]: res.rows ?? [] }));
      setFirmContactsSearch((prev) => (prev[firmKey] != null ? prev : { ...prev, [firmKey]: '' }));
    } finally {
      setFirmContactsLoading((prev) => ({ ...prev, [firmKey]: false }));
    }
  }

  if (loading && ((view === 'contacts' && contacts.length === 0) || (view === 'firms' && firms.length === 0))) {
    return <div className="loading-page"><div className="spinner" /><span>Loading {view}…</span></div>;
  }
  if (error) {
    return <div className="empty-state"><div className="empty-state__icon">⚠️</div><div className="empty-state__title">Failed to load {view}</div><div className="empty-state__sub">{error}</div></div>;
  }

  const rows = view === 'contacts' ? visibleContacts : firms;
  const total = view === 'contacts' ? contactsTotal : firmsTotal;
  const columns = view === 'contacts' ? CONTACT_COLUMNS : FIRM_COLUMNS;
  const visibleCols = view === 'contacts' ? contactsColumns : firmsColumns;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedIds = view === 'contacts' ? selectedContactIds : selectedFirmIds;
  const selectedCount = selectedIds.size;
  const selectedFirmRows = rows.filter((r) => r.firm_id && selectedFirmIds.has(r.firm_id));
  const selectedFirmLinkedContacts = selectedFirmRows.reduce((sum, r) => sum + Number(r.contact_count ?? 0), 0);

  if (view === 'firms') {
    return (
      <div className="contacts-page">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn--sm btn--ghost" onClick={() => { setView('contacts'); setPage(1); }}>👤 Contacts</button>
          <button className="btn btn--sm btn--primary" onClick={() => { setView('firms'); setPage(1); }}>🏢 Firms</button>
        </div>
        <input className="filter-bar__search" placeholder="Search firms…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ flex: 1, minWidth: 200, maxWidth: 340 }} />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`btn btn--sm ${firmLayout === 'list' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setFirmLayout('list')}>List</button>
          <button className={`btn btn--sm ${firmLayout === 'grid' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setFirmLayout('grid')}>Grid</button>
        </div>
          <button className="btn btn--ghost btn--sm" onClick={() => setColumnsOpen((v) => !v)}>Columns</button>
          <button
            className={`btn btn--sm ${filterPanelOpen ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setFilterPanelOpen((v) => !v)}
          >
            {totalActiveFirmFilters > 0 ? `Filters (${totalActiveFirmFilters})` : 'Filters'}
          </button>
          {hasActiveFirmFilters && !filterPanelOpen && (
            <button className="btn btn--ghost btn--sm" onClick={clearAllFirmFilters} style={{ color: 'var(--c-primary)' }}>✕ Clear</button>
          )}
          {(selectedFirmIds.size > 0 || selectAllFirmResults) && (
            <span ref={exportWrapRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button ref={exportBtnRef} className="btn btn--ghost btn--sm" onClick={() => setExportOpen((v) => !v)}>Export ▾</button>
              {exportOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 9999, background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,.15)', padding: 6, marginTop: 4 }}>
                  <button onClick={handleExportAll} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent' }}>Export all columns</button>
                </div>
              )}
            </span>
          )}
          <span ref={syncMenuWrapRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => { setSyncMenuOpen((v) => !v); setSyncResult(null); }}
              disabled={isSyncing}
              title="Sync with Google Sheet"
            >
              {isSyncing ? '↕ Syncing…' : '↕ Sync ▾'}
            </button>
            {syncMenuOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 9999, background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10, minWidth: 290, boxShadow: '0 8px 24px rgba(0,0,0,.18)', padding: 6, marginTop: 4 }}>
                <div style={{ padding: '6px 12px 4px', fontSize: 11, fontWeight: 700, color: 'var(--c-text-2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sync with Google Sheet</div>
                {[
                  { key: 'syncScores', label: '↻ Sync Scores', desc: 'Update BQ firm scores from Fund tab', fn: syncFirmScores, userLabel: 'Scores synced' },
                  { key: 'firmsSheetToBq', label: '⬇ Import from Sheet', desc: 'Add new firms in Sheet that are missing from BQ', fn: syncFirmsSheetToBq, userLabel: 'Imported' },
                  { key: 'firmsBqToSheet', label: '⬆ Export to Sheet', desc: 'Append BQ firms that are missing from Sheet', fn: syncFirmsBqToSheet, userLabel: 'Exported' },
                ].map(({ key, label, desc, fn, userLabel }) => (
                  <button
                    key={key}
                    disabled={isSyncing}
                    onClick={() => { setSyncMenuOpen(false); handleSync(key, fn, userLabel); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', borderRadius: 7, border: 'none', cursor: isSyncing ? 'not-allowed' : 'pointer', background: 'transparent', opacity: isSyncing ? 0.5 : 1 }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{syncing[key] ? label.replace(/^[↻⬇⬆] /, '') + '…' : label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--c-text-2)', marginTop: 1 }}>{desc}</div>
                  </button>
                ))}
              </div>
            )}
          </span>
          {syncResult && (
            <span
              style={{
                fontSize: 12, padding: '2px 10px', borderRadius: 999, fontWeight: 600,
                background: syncResult.ok ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)',
                color: syncResult.ok ? '#16a34a' : '#dc2626',
                border: `1px solid ${syncResult.ok ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'}`,
                cursor: syncResult.conflicts?.length > 0 ? 'pointer' : 'default',
              }}
              onClick={() => syncResult.conflicts?.length > 0 && setConflictsOpen(true)}
              title={syncResult.conflicts?.length > 0 ? 'Click to view conflicts' : undefined}
            >
              {syncResult.msg}{syncResult.conflicts?.length > 0 ? ' — view conflicts' : ''}
            </span>
          )}
          <button className="btn btn--ghost btn--sm" onClick={onRefresh}>↻ Reload</button>
        </div>

        {(selectedFirmIds.size > 0 || selectAllFirmResults) && (
          <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--c-text-2)', display: 'flex', alignItems: 'center', gap: 10 }}>
              {selectAllFirmResults ? (
                <>
                  <span>All <strong>{total.toLocaleString()}</strong> firms selected.</span>
                  <button className="btn btn--ghost btn--sm" onClick={() => { setSelectAllFirmResults(false); setSelectedFirmIds(new Set()); }}>Clear selection</button>
                </>
              ) : (
                <>
                  <span><strong>{selectedFirmIds.size}</strong> firm{selectedFirmIds.size === 1 ? '' : 's'} on this page selected.</span>
                  {total > rows.length && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setSelectAllFirmResults(true)}>
                      Select all {total.toLocaleString()} results
                    </button>
                  )}
                </>
              )}
            </div>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => openDeleteConfirm(
                'firms',
                [...selectedFirmIds],
                `${selectAllFirmResults ? total.toLocaleString() : selectedFirmIds.size} firm${selectedFirmIds.size === 1 ? '' : 's'} will be deleted. ${selectedFirmLinkedContacts.toLocaleString()} linked contact${selectedFirmLinkedContacts === 1 ? '' : 's'} will keep their records, but their firm assignment will be cleared.`
              )}
              disabled={deletingFirms}
              style={{ color: 'var(--c-red)', borderColor: 'rgba(239,68,68,.35)' }}
            >
              Delete selected firms
            </button>
          </div>
        )}

        {columnsOpen && (
          <div className="card" style={{ padding: 12, marginBottom: 12 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>Firm Columns</strong>
                <button className="btn btn--ghost btn--sm" onClick={() => setFirmsColumns(new Set(FIRM_COLUMNS))}>Show All</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                {FIRM_COLUMNS.map((col) => (
                  <label key={col} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                    <input type="checkbox" checked={visibleCols.has(col)} onChange={() => toggleColumn(col)} />
                    <span>{compactLabel(col)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
          {filterPanelOpen && (
            <FilterPanel
              view="firms"
              colFilters={colFilters}
              firmColFilters={firmColFilters}
              onColFilterChange={(col, vals) => { setColFilters((prev) => ({ ...prev, [col]: vals })); setPage(1); }}
              onFirmColFilterChange={(col, vals) => { setFirmColFilters((prev) => ({ ...prev, [col]: vals })); setPage(1); }}
              onClearAll={clearAllFirmFilters}
              onClose={() => setFilterPanelOpen(false)}
              getToken={getToken}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--c-text-3)' }}>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}-{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()} rows
                {(loading || filtersPending) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--c-text-3)' }}>
                    <span className="spinner" style={{ width: 11, height: 11 }} />
                    updating…
                  </span>
                )}
              </div>
            </div>

        {firmLayout === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {rows.map((row, i) => (
              <div key={row.firm_id ?? i} className="card" style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={selectedFirmIds.has(row.firm_id)}
                      onChange={() => toggleSelected(setSelectedFirmIds, row.firm_id)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{row.firm_name ?? '—'}</div>
                  </div>
                  <button className="btn btn--ghost btn--sm" onClick={() => toggleFirmExpanded(row.firm_id ?? row.firm_name, row.firm_name)}>{expandedFirmIds.has(row.firm_id ?? row.firm_name) ? 'Hide' : 'Show'}</button>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {FIRM_COLUMNS.filter((col) => visibleCols.has(col)).map((col) => (
                    <div key={col} style={{ fontSize: 12.5 }}>
                      <span style={{ color: 'var(--c-text-3)' }}>{compactLabel(col)}:</span> {renderFirmValue(row, col)}
                    </div>
                  ))}
                </div>
                {expandedFirmIds.has(row.firm_id ?? row.firm_name) && (
                  <div style={{ marginTop: 12, borderTop: '1px solid var(--c-border)', paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Linked contacts</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {(firmContactsLoading[row.firm_id ?? row.firm_name] ? [] : (firmContactsById[row.firm_id ?? row.firm_name] ?? [])).map((c) => (
                        <div key={c.contact_id}>
                          <button className="link-cell" style={{ fontWeight: 600, textAlign: 'left' }} onClick={() => onOpenTimeline?.(c)}>{c.name ?? '—'}</button>
                        </div>
                      ))}
                      {firmContactsLoading[row.firm_id ?? row.firm_name] && <div style={{ color: 'var(--c-text-3)', fontSize: 12 }}>Loading contacts…</div>}
                      {!firmContactsLoading[row.firm_id ?? row.firm_name] && (firmContactsById[row.firm_id ?? row.firm_name] ?? []).length === 0 && (
                        <div style={{ color: 'var(--c-text-3)', fontSize: 12 }}>No linked contacts</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        ref={firmHeaderCheckRef}
                        type="checkbox"
                        checked={selectAllFirmResults || (rows.length > 0 && rows.every((r) => r.firm_id && selectedFirmIds.has(r.firm_id)))}
                        onChange={(e) => {
                          if (selectAllFirmResults) {
                            setSelectAllFirmResults(false);
                            setSelectedFirmIds(new Set());
                          } else if (e.target.checked) {
                            setSelectedFirmIds(new Set(rows.filter((r) => r.firm_id).map((r) => r.firm_id)));
                          } else {
                            setSelectAllFirmResults(false);
                            setSelectedFirmIds(new Set());
                          }
                        }}
                      />
                    </th>
                    {FIRM_COLUMNS.map((col) => visibleCols.has(col) ? (
                      <th key={col} style={{
                        position: 'relative', whiteSpace: col === 'main_coverage_count' ? 'normal' : 'nowrap',
                        width: firmColWidths[col] ?? (col === 'firm_name' ? 170 : col === 'bd_status' ? 120 : 90),
                        minWidth: firmColWidths[col] ?? (col === 'firm_name' ? 170 : col === 'bd_status' ? 100 : 90),
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingRight: 8 }}>
                          <span onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', lineHeight: 1.3 }}>
                            {col === 'main_coverage_count'
                              ? <># Contacts<br/>(Main Coverage) {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</>
                              : <>{compactLabel(col)} {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</>
                            }
                          </span>
                          {FIRM_HEADER_FILTER_COLS[col] && (
                            <HeaderFilterDropdown
                              col={col}
                              type={FIRM_HEADER_FILTER_COLS[col]}
                              table="firms"
                              selected={firmColFilters[col] ?? (FIRM_HEADER_FILTER_COLS[col] === 'range' ? { min: '', max: '' } : [])}
                              onChange={(vals) => { setFirmColFilters((prev) => ({ ...prev, [col]: vals })); setPage(1); }}
                              getToken={getToken}
                            />
                          )}
                        </div>
                        <ResizeHandle col={col} onResize={handleFirmResize} />
                      </th>
                    ) : null)}
                    <th>Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const firmKey = row.firm_id ?? row.firm_name;
                    const isExpanded = expandedFirmIds.has(firmKey);
                    const isLoading = firmContactsLoading[firmKey];
                    const contacts = firmContactsById[firmKey] ?? [];
                    const searchTerm = String(firmContactsSearch[firmKey] ?? '').trim().toLowerCase();
                    const filteredContacts = searchTerm
                      ? contacts.filter((c) => {
                        const haystack = [
                          c.name,
                          c.email,
                          c.role,
                          c._stage,
                          c.bd_status,
                          c.firm_name,
                          c.sector_coverage,
                          c.industry_focus,
                        ].map((v) => String(v ?? '').toLowerCase()).join(' ');
                        return haystack.includes(searchTerm);
                      })
                      : contacts;
                    const colSpanTotal = 1 + FIRM_COLUMNS.filter((c) => visibleCols.has(c)).length + 1;
                    return (
                      <>
                        <tr key={row.firm_id ?? i}>
                          <td style={{ width: 36 }}>
                            <input
                              type="checkbox"
                              checked={selectedFirmIds.has(row.firm_id)}
                              onChange={() => toggleSelected(setSelectedFirmIds, row.firm_id)}
                            />
                          </td>
                          {FIRM_COLUMNS.map((col) => visibleCols.has(col) ? (
                            <td key={col} style={{ whiteSpace: 'normal', verticalAlign: 'top', overflow: 'hidden' }}>
                              {renderFirmValue(row, col)}
                            </td>
                          ) : null)}
                          <td>
                            <button className="btn btn--ghost btn--sm" onClick={() => toggleFirmExpanded(firmKey, row.firm_name)}>
                              {isExpanded ? 'Hide contacts' : 'Show contacts'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${row.firm_id ?? i}-expanded`}>
                            <td colSpan={colSpanTotal} style={{ padding: 0, borderTop: 'none' }}>
                              <div style={{ padding: '12px 16px 14px', background: 'var(--c-bg-2)' }}>
                                {isLoading && <div style={{ color: 'var(--c-text-3)', fontSize: 12 }}>Loading contacts…</div>}
                                {!isLoading && (
                                  <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text-2)' }}>
                                        {filteredContacts.length.toLocaleString()} contact{filteredContacts.length === 1 ? '' : 's'}
                                      </div>
                                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 240, flex: '1 1 320px' }}>
                                        <input
                                          value={firmContactsSearch[firmKey] ?? ''}
                                          onChange={(e) => setFirmContactsSearch((prev) => ({ ...prev, [firmKey]: e.target.value }))}
                                          placeholder="Search contacts in this firm…"
                                          className="filter-bar__search"
                                          style={{ flex: 1, minWidth: 220 }}
                                        />
                                        {(firmContactsSearch[firmKey] ?? '') && (
                                          <button
                                            className="btn btn--ghost btn--sm"
                                            onClick={() => setFirmContactsSearch((prev) => ({ ...prev, [firmKey]: '' }))}
                                          >
                                            Clear
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {contacts.length === 0 && <div style={{ color: 'var(--c-text-3)', fontSize: 12 }}>No linked contacts</div>}
                                    {contacts.length > 0 && filteredContacts.length === 0 && (
                                      <div style={{ color: 'var(--c-text-3)', fontSize: 12 }}>No contacts match your search</div>
                                    )}
                                    {filteredContacts.length > 0 && (
                                      <div style={{ overflowX: 'auto', border: '1px solid var(--c-border)', borderRadius: 12, background: 'var(--c-surface)', boxShadow: '0 1px 2px rgba(0,0,0,.03)' }}>
                                        <table className="data-table" style={{ margin: 0 }}>
                                          <thead>
                                            <tr>
                                              <th style={{ minWidth: 180 }}>Name</th>
                                              <th style={{ minWidth: 220 }}>Email</th>
                                              <th style={{ minWidth: 160 }}>Role</th>
                                              <th style={{ minWidth: 110 }}>Stage</th>
                                              <th style={{ minWidth: 100 }}>Main Coverage</th>
                                              <th style={{ minWidth: 160 }}>Coverage</th>
                                              <th style={{ minWidth: 160 }}>Industry Focus</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {filteredContacts.map((c) => (
                                              <tr key={c.contact_id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                  <button className="link-cell" style={{ fontWeight: 600, textAlign: 'left' }} onClick={() => onOpenTimeline?.(c)}>
                                                    {c.name ?? '—'}
                                                  </button>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', color: 'var(--c-text-2)' }}>{c.email ?? '—'}</td>
                                                <td style={{ whiteSpace: 'normal' }}>{c.role ?? '—'}</td>
                                                <td>
                                                  {c._stage ? (
                                                    <StagePicker contact={c} stage={c._stage} getToken={getToken} onOverride={handleStageOverride} />
                                                  ) : '—'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', fontWeight: c.main_coverage === 1 ? 700 : 400, color: c.main_coverage === 1 ? 'var(--c-primary)' : 'var(--c-text-2)' }}>
                                                  {c.main_coverage ?? 0}
                                                </td>
                                                <td style={{ whiteSpace: 'normal', fontSize: 12, color: 'var(--c-text-2)' }}>{c.sector_coverage ?? '—'}</td>
                                                <td style={{ whiteSpace: 'normal', fontSize: 12, color: 'var(--c-text-2)' }}>{c.industry_focus ?? '—'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button className="btn btn--ghost btn--sm" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="btn btn--ghost btn--sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹ Prev</button>
          <span style={{ fontSize: 13, color: 'var(--c-text-2)' }}>Page {page} of {totalPages}</span>
          <button className="btn btn--ghost btn--sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next ›</button>
          <button className="btn btn--ghost btn--sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
        </div>
          </div>{/* end flex: 1 inner */}
        </div>{/* end flex row with FilterPanel */}
      </div>
    );
  }

  return (
    <div className="contacts-page">
      {pipelineMode && view === 'contacts' && pipelineStats && (
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 20 }}>
          <div className="stat-card stat-card--orange">
            <div className="stat-val">{contactsTotal.toLocaleString()}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card stat-card--green">
            <div className="stat-val">{formatCurrencyFlexible(pipelineStats.weightedArr / 1_000_000)}</div>
            <div className="stat-label">Weighted ARR</div>
          </div>
          {['trial', 'demo', 'warm', 'customer'].map((stage) => (
            <div key={stage} className={`stat-card stat-card--${PIPELINE_STAT_COLORS[stage]}`}>
              <div className="stat-val">{pipelineStats.stageCounts[stage] ?? 0}</div>
              <div className="stat-label">{STAGE_LABELS[stage]}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className={`btn btn--sm ${view === 'contacts' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => { setView('contacts'); setPage(1); }}>👤 Contacts</button>
          <button className={`btn btn--sm ${view === 'firms' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => { setView('firms'); setPage(1); }}>🏢 Firms</button>
          {view === 'contacts' && (
            <>
              <ViewDropdown
                views={savedViews}
                activeViewId={activeViewId}
                dirty={viewDirty}
                onSelect={applyView}
                onSaveNew={() => setSaveModal(true)}
                onUpdateCurrent={handleUpdateCurrentView}
                onRename={handleRenameView}
                onDelete={handleDeleteView}
              />
              {viewSaveMsg && (
                <span style={{
                  fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                  background: viewSaveMsg.ok ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)',
                  color: viewSaveMsg.ok ? '#16a34a' : '#dc2626',
                  border: `1px solid ${viewSaveMsg.ok ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'}`,
                  transition: 'opacity .2s',
                }}>
                  {viewSaveMsg.ok ? '✓' : '⚠'} {viewSaveMsg.text}
                </span>
              )}
            </>
          )}
        </div>
        <input
          className="filter-bar__search"
          placeholder={view === 'contacts' ? 'Search contacts…' : 'Search firms…'}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ flex: 1, minWidth: 200, maxWidth: 340 }}
        />
        <button className="btn btn--ghost btn--sm" onClick={() => setColumnsOpen((v) => !v)}>Columns</button>
        <button
          className={`btn btn--sm ${filterPanelOpen ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setFilterPanelOpen((v) => !v)}
        >
          {totalActiveContactFilters > 0 ? `Filters (${totalActiveContactFilters})` : 'Filters'}
        </button>
        {view === 'contacts' && (
          <button
            className={`btn btn--sm ${pipelineMode ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => togglePipelineMode(!pipelineMode)}
            title="Show only customer/trial/demo stage contacts with pipeline stats"
          >
            📊 Pipeline view
          </button>
        )}
        {hasActiveContactFilters && !filterPanelOpen && (
          <button className="btn btn--ghost btn--sm" onClick={clearAllContactFilters} style={{ color: 'var(--c-primary)' }}>✕ Clear</button>
        )}
        {(selectedContactIds.size > 0 || selectAllContactResults) && (
          <span ref={exportWrapRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button ref={exportBtnRef} className="btn btn--ghost btn--sm" onClick={() => setExportOpen((v) => !v)}>Export ▾</button>
            {exportOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 9999, background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, minWidth: 190, boxShadow: '0 8px 24px rgba(0,0,0,.15)', padding: 6, marginTop: 4 }}>
                <button onClick={handleExportAll} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent' }}>Export all columns</button>
                <button onClick={handleExportForGmail} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent' }}>Export for Gmail</button>
                <button onClick={() => { setExportOpen(false); setBigDoughModalOpen(true); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent' }}>Export for BigDough</button>
              </div>
            )}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setSyncPanelOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            borderRadius: 8, border: '1px solid var(--border,#e2e8f0)', background: 'var(--bg-primary,#fff)',
            cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-primary,#1a1a1a)',
          }}
        >
          <span style={{ fontSize: 15 }}>⇅</span> Sync
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => setDedupOpen(true)}>⊕ Find Duplicates</button>
        <button
          className="btn btn--ghost btn--sm"
          disabled={backfillFirmStatus === 'loading'}
          title="Fill blank status/city/state/country/AUM on contacts from their firm or a sibling contact at the same firm"
          onClick={() => {
            setBackfillFirmStatus('loading');
            backfillFirmDefaults(getToken)
              .then((r) => { setBackfillFirmStatus(r); onRefresh?.(); })
              .catch((e) => setBackfillFirmStatus({ error: e.message }));
          }}
        >
          {backfillFirmStatus === 'loading'
            ? 'Filling…'
            : backfillFirmStatus?.error
              ? `✗ ${backfillFirmStatus.error}`
              : backfillFirmStatus?.result
                ? `✓ filled (${Object.entries(backfillFirmStatus.result).map(([f, v]) => `${f}:${v.rows_updated}`).join(', ')})`
                : '🏢 Backfill Firm Defaults'}
        </button>
        <button
          className="btn btn--ghost btn--sm"
          disabled={exporting}
          title="Download the current filtered view (all rows) as CSV"
          onClick={handleExportCsv}
        >
          {exporting ? 'Exporting…' : '⬇ CSV'}
        </button>
        <button
          className="btn btn--ghost btn--sm"
          title="Count grid over the current filtered view — pick row/column dimensions, then copy or download"
          onClick={() => setBreakdownOpen(true)}
        >
          ▦ Breakdown
        </button>
        <button
          className="btn btn--ghost btn--sm"
          title="Split the current filtered view into outreach batches — paste sizes from the planning sheet; email domains spread across batches"
          onClick={() => setBatchSplitOpen(true)}
        >
          🎲 Batches
        </button>
        <button className="btn btn--ghost btn--sm" onClick={onRefresh}>↻ Reload</button>
        <button className="btn btn--primary btn--sm" onClick={() => onOpenAddContact?.()}>+ Add Contact</button>
      </div>

      {columnsOpen && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>{view === 'contacts' ? 'Contact' : 'Firm'} Columns</strong>
              <button className="btn btn--ghost btn--sm" onClick={toggleAllColumns}>Show All</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
              {displayColumns.map((col) => (
                <label key={col} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                  <input type="checkbox" checked={visibleCols.has(col)} onChange={() => toggleColumn(col)} />
                  <span>{compactLabel(col)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}


      <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
        {filterPanelOpen && (
          <FilterPanel
            view={view}
            colFilters={colFilters}
            firmColFilters={firmColFilters}
            onColFilterChange={(col, vals) => { setColFilters((prev) => ({ ...prev, [col]: vals })); setPage(1); }}
            onFirmColFilterChange={(col, vals) => { setFirmColFilters((prev) => ({ ...prev, [col]: vals })); setPage(1); }}
            onClearAll={clearAllContactFilters}
            onClose={() => setFilterPanelOpen(false)}
            getToken={getToken}
            tagFilterModes={tagFilterModes}
            onSetTagMode={setTagMode}
            textExcludeCols={textExcludeCols}
            onToggleTextExclude={toggleTextExclude}
            getFilters={buildFilters}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
      {parentFundMatches.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 8, lineHeight: 1.6 }}>
          {parentFundMatches.map((p) => (
            <div key={p.firm_id}>
              🏢 <b>{p.name}</b> sub-funds: {p.children.length > 0 ? p.children.join(', ') : '—'}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--c-text-3)' }}>
          Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}-{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()} rows
          {(loading || filtersPending) && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--c-text-3)' }}>
              <span className="spinner" style={{ width: 11, height: 11 }} />
              updating…
            </span>
          )}
        </div>
      </div>

      {(selectedContactIds.size > 0 || selectAllContactResults) && (
        <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', display: 'flex', alignItems: 'center', gap: 10 }}>
            {selectAllContactResults ? (
              <>
                <span>All <strong>{total.toLocaleString()}</strong> contacts selected.</span>
                <button className="btn btn--ghost btn--sm" onClick={() => { setSelectAllContactResults(false); setSelectedContactIds(new Set()); }}>Clear selection</button>
              </>
            ) : (
              <>
                <span><strong>{selectedContactIds.size}</strong> contact{selectedContactIds.size === 1 ? '' : 's'} on this page selected.</span>
                {total > rows.length && (
                  <button className="btn btn--ghost btn--sm" onClick={() => setSelectAllContactResults(true)}>
                    Select all {total.toLocaleString()} results
                  </button>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Single-selection actions — same as the row's ✏️/🏢 buttons, so
                you don't have to scroll the table to reach the actions column. */}
            {!selectAllContactResults && selectedContactIds.size === 1 && (() => {
              const sel = rows.find((r) => r.contact_id && selectedContactIds.has(r.contact_id));
              if (!sel) return null;
              return (
                <>
                  <button className="btn btn--ghost btn--sm" onClick={() => onEditContact?.(sel)}>✏️ Edit</button>
                  <button className="btn btn--ghost btn--sm" title="Move to new firm (keeps old firm as history)" onClick={() => setMoveFirmContact(sel)}>🏢 Move firm</button>
                </>
              );
            })()}
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => openDeleteConfirm(
                'contacts',
                [...selectedContactIds],
                `${selectAllContactResults ? total.toLocaleString() : selectedContactIds.size} contact${selectedContactIds.size === 1 ? '' : 's'} will be deleted everywhere — removed from all pages, trials deleted, product account locked. BD imports will not re-add them.`
              )}
              disabled={deletingContacts}
              style={{ color: 'var(--c-red)', borderColor: 'rgba(239,68,68,.35)' }}
            >
              Delete selected contacts
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    ref={contactHeaderCheckRef}
                    type="checkbox"
                    checked={selectAllContactResults || (rows.length > 0 && rows.every((c) => c.contact_id && selectedContactIds.has(c.contact_id)))}
                    onChange={(e) => {
                      if (selectAllContactResults) {
                        setSelectAllContactResults(false);
                        setSelectedContactIds(new Set());
                      } else if (e.target.checked) {
                        setSelectedContactIds(new Set(rows.filter((c) => c.contact_id).map((c) => c.contact_id)));
                      } else {
                        setSelectAllContactResults(false);
                        setSelectedContactIds(new Set());
                      }
                    }}
                  />
                </th>
                {displayColumns.map((col) => visibleCols.has(col) ? (
                  <th key={col} style={{
                    position: 'relative', whiteSpace: 'nowrap',
                    width: contactColWidths[col] ?? (col === 'name' ? 170 : col === 'industry_focus' ? 220 : 'auto'),
                    minWidth: contactColWidths[col] ?? (col === 'industry_focus' ? 180 : 90),
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingRight: 8 }}>
                      {(() => { const fc = CONTACT_COL_ALIAS[col] ?? col; return (<>
                      <span
                        onClick={() => handleSort(col)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={col === 'bd_aum_personal' ? 'Self-reported by the contact in BigDough — not independently verified' : undefined}
                      >
                        {col === '_recent_touch_count' ? `Touches (${touchWindowShort(colFilters._recent_touch_count?.window)})` : compactLabel(col)}{col === 'bd_aum_personal' ? ' ⚠' : ''} {sortKey === fc ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                      <HeaderFilterDropdown
                        col={fc}
                        type={HEADER_FILTER_COLS[fc] ?? 'text'}
                        table="contacts"
                        selected={colFilters[fc] ?? (HEADER_FILTER_COLS[fc] === 'range' ? { min: '', max: '', includeNull: false, onlyHasVal: false } : HEADER_FILTER_COLS[fc] === 'date' ? { after: '', before: '', nullMode: 'all' } : [])}
                        onChange={(vals) => { setColFilters((prev) => ({ ...prev, [fc]: vals })); setPage(1); }}
                        getToken={getToken}
                        tagMode={tagFilterModes[fc] ?? 'any'}
                        onSetTagMode={(mode) => setTagMode(fc, mode)}
                        otherFilters={buildFilters().filter((f) => f.col !== fc)}
                      /></>);})()}
                    </div>
                    <ResizeHandle col={col} onResize={handleContactResize} />
                  </th>
                ) : null)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isHighlighted = row.contact_id && row.contact_id === flashContactId;
                return (
                <tr
                  key={row.contact_id ?? row.firm_id ?? i}
                  ref={isHighlighted ? highlightRowRef : null}
                  style={isHighlighted ? { animation: 'highlight-flash 2.5s ease-out forwards', background: 'rgba(251,146,60,.12)' } : undefined}
                  onDoubleClick={() => onEditContact?.(row)}
                  title="Double-click row to edit"
                >
                  <td style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selectAllContactResults || (!!row.contact_id && selectedContactIds.has(row.contact_id))}
                      onChange={() => row.contact_id && toggleSelected(setSelectedContactIds, row.contact_id)}
                    />
                  </td>
                  {displayColumns.map((col) => visibleCols.has(col) ? (
                    <td key={col} style={{ whiteSpace: 'normal', verticalAlign: 'top', overflow: 'hidden' }}>
                      {col === 'name' ? (
                        <div className="contact-name-cell">
                          <div className="contact-initials">{getInitials(row.name)}</div>
                          <div>
                            <div className="link-cell" style={{ fontWeight: 600 }} onClick={() => onOpenTimeline?.(row)}>{row.name ?? '—'}</div>
                          </div>
                        </div>
                      ) : col === 'firm_name' ? row._firm_name ?? row.firm_name ?? '—'
                      : col === 'bd_status' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <StatusPicker
                              value={row.bd_status ?? ''}
                              saving={savingStatusId === row.contact_id}
                              onSave={(s) => handleContactStatusSave(row.contact_id, s)}
                            />
                            {row._parent_firm_name && (
                              <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--c-text-3)', background: 'var(--c-bg-2)', border: '1px solid var(--c-border)', borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                                ↑ {row._parent_firm_name}
                              </span>
                            )}
                          </span>
                        )
                      : col === 'fund_type' ? row._fund_type ?? row.fund_type ?? '—'
                      : col === '_firm_aum' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            {formatAumBn(row._firm_aum)}
                            {row._parent_firm_name && (
                              <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--c-text-3)', background: 'var(--c-bg-2)', border: '1px solid var(--c-border)', borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                                ↑ {row._parent_firm_name}
                              </span>
                            )}
                          </span>
                        )
                      : col === 'deal_tier' ? (row.deal_tier ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <span
                              title={[row.deal_next_step, row.deal_why].filter(Boolean).join(' · ')}
                              style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                                background: DEAL_TIER_BADGE[row.deal_tier]?.bg ?? '#f1f5f9',
                                color: DEAL_TIER_BADGE[row.deal_tier]?.fg ?? '#64748b',
                                opacity: row.deal_confidence === 'low' ? 0.65 : 1,
                                alignSelf: 'flex-start' }}
                            >
                              {row.deal_tier}{row.deal_trend === 'up' ? ' ↑' : row.deal_trend === 'down' ? ' ↓' : ''}
                            </span>
                            {(row.deal_next_step || row.deal_why) && (
                              <span style={{ fontSize: 10.5, color: 'var(--c-text-3)', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                                {row.deal_next_step || row.deal_why}
                              </span>
                            )}
                          </div>
                        ) : '—')
                      : col === 'main_coverage' ? (row.main_coverage != null ? String(row.main_coverage) : '—')
                      : col === 'relevance_score' ? (row.relevance_score ?? '—')
                      : col === '_stage' ? (
                          <StagePicker
                            contact={row}
                            stage={row._stage ?? 'cold-untouched'}
                            getToken={getToken}
                            onOverride={handleStageOverride}
                          />
                        )
                      : col === '_bd_high_intent' ? (
                          row._bd_high_intent
                            ? <HighIntentChip row={row} />
                            : <span style={{ color: 'var(--c-text-3)' }}>—</span>
                        )
                      : col === '_recent_touch_count' ? (
                          <span style={{ fontWeight: 600, color: row._recent_touch_count > 0 ? 'var(--c-primary)' : 'var(--c-text-3)', fontSize: 13 }}>
                            {row._recent_touch_count ?? 0}
                          </span>
                        )
                      : col === '_touches' ? (
                          // Explicit numeric render — the fmt() fallback would
                          // date-parse the count (4 → "Dec 31, 69").
                          <span style={{ fontWeight: 600, color: row._touches > 0 ? 'var(--c-text-2)' : 'var(--c-text-3)', fontSize: 13 }}>
                            {row._touches ?? 0}
                          </span>
                        )
                      : col === '_last_touch_ts' ? fmt(getLastTouch(row))
                      : col === '_last_email_open_ts' ? fmt(row._last_email_open_ts)
                      : col === 'bd_aum_personal' ? (
                          row.bd_aum_personal == null || row.bd_aum_personal === '' ? '—' : (
                            <span
                              style={{ color: 'var(--c-text-3)', fontStyle: 'italic' }}
                              title="Self-reported by the contact in BigDough — not independently verified. Prefer Firm AUM / Account AUM when comparing."
                            >
                              {formatAumBn(row.bd_aum_personal)}
                            </span>
                          )
                        )
                      : col === 'account_aum' ? formatAumBn(row.account_aum)
                      : col === '_firm_owner' ? (row._firm_owner ?? '—')
                      : col === 'email' ? (row.email || (() => {
                          const sug = getEmailSuggestion(row);
                          return sug ? (
                            <button
                              type="button"
                              className="email-suggest-chip"
                              title={`No email on file — guessed from ${sug.count} firm-mate${sug.count === 1 ? '' : 's'} (e.g. ${sug.example.email}).${sug.conflict ? ` The firm's usual pattern gives ${sug.conflict.email}, but that's already ${sug.conflict.name}'s.` : ''} Click to review & save.`}
                              onClick={(e) => { e.stopPropagation(); setEmailSuggestContact(row); }}
                            >
                              {sug.email}?
                            </button>
                          ) : '—';
                        })())
                      : col === 'source' ? (row.source ?? '—')
                      : TAG_COLUMNS.has(col) ? renderTagCell(row[col], col)
                      : fmt(row[col])}
                    </td>
                  ) : null)}
                  <td>
                    <button className="action-btn" title="Edit contact" onClick={() => onEditContact?.(row)}>✏️</button>
                    <button className="action-btn" title="Move to new firm (keeps old firm as history)" onClick={() => setMoveFirmContact(row)}>🏢</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button className="btn btn--ghost btn--sm" onClick={() => setPage(1)} disabled={page === 1}>«</button>
        <button className="btn btn--ghost btn--sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹ Prev</button>
        <span style={{ fontSize: 13, color: 'var(--c-text-2)' }}>Page {page} of {totalPages}</span>
        <button className="btn btn--ghost btn--sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next ›</button>
        <button className="btn btn--ghost btn--sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
      </div>
        </div>{/* end flex: 1 inner */}
      </div>{/* end flex row with FilterPanel */}

      {conflictsOpen && syncResult?.conflicts?.length > 0 && (
        <div className="panel-overlay" onClick={() => setConflictsOpen(false)}>
          <div
            className="panel"
            style={{ width: 720, maxWidth: '95vw', height: 'auto', maxHeight: '80vh', margin: 'auto', borderRadius: 16 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel__header">
              <div className="panel__title">Sync conflicts — BigQuery wins, no changes made</div>
              <button className="panel__close" onClick={() => setConflictsOpen(false)}>×</button>
            </div>
            <div className="panel__body" style={{ overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: 'var(--c-text-2)', marginBottom: 12 }}>
                These rows exist in both Sheet and BigQuery but have different values. BigQuery is the source of truth — no changes were made. Review these manually if needed.
              </p>
              <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Key (email / name)', 'Field', 'BigQuery value', 'Sheet value'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '4px 10px', borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {syncResult.conflicts.map((c, i) => (
                    <tr key={i} style={{ background: i % 2 ? 'var(--c-surface-2)' : undefined }}>
                      <td style={{ padding: '3px 10px', color: 'var(--c-text-2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.key}</td>
                      <td style={{ padding: '3px 10px', fontFamily: 'monospace', fontSize: 11.5 }}>{c.field}</td>
                      <td style={{ padding: '3px 10px', color: 'var(--c-primary)', fontWeight: 600 }}>{String(c.bq_value ?? '')}</td>
                      <td style={{ padding: '3px 10px', color: 'var(--c-text-2)' }}>{String(c.sheet_value ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="panel-overlay" onClick={() => setConfirmModal(null)}>
          <div
            className="panel"
            style={{ width: 520, maxWidth: '95vw', height: 'auto', maxHeight: '90vh', margin: 'auto', borderRadius: 16 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel__header">
              <div className="panel__title">Confirm delete</div>
              <button className="panel__close" onClick={() => setConfirmModal(null)}>×</button>
            </div>
            <div className="panel__body">
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 30, lineHeight: 1 }}>⚠️</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>This action cannot be undone.</div>
                  <div style={{ color: 'var(--c-text-2)', fontSize: 13.5, lineHeight: 1.5 }}>{confirmModal.impact}</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
                <button className="btn btn--ghost btn--sm" onClick={() => setConfirmModal(null)}>Cancel</button>
                <button className="btn btn--primary btn--sm" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <SyncPanel
        open={syncPanelOpen}
        onClose={() => setSyncPanelOpen(false)}
        onDone={() => { setRefreshKey((k) => k + 1); }}
        getToken={getToken}
      />

      {/* Trial auto-created toast */}
      {trialCreatedToast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, display: 'flex', alignItems: 'center', gap: 12,
          background: 'var(--c-surface)', border: `1px solid ${trialCreatedToast.error ? 'var(--red)' : 'var(--c-border)'}`,
          borderRadius: 12, padding: '12px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,.18)', fontSize: 13,
          animation: 'slideUpFade .25s ease',
        }}>
          <span style={{ fontSize: 16 }}>{trialCreatedToast.error ? '⚠' : '✓'}</span>
          <span style={{ color: 'var(--c-text-2)' }}>
            {trialCreatedToast.error
              ? <>Trial creation failed: <strong>{trialCreatedToast.error}</strong> — check console for details</>
              : <>Trial created · ends <strong>{trialCreatedToast.endDate}</strong> · open the Trials tab to add details</>
            }
          </span>
          <button
            onClick={() => setTrialCreatedToast(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--c-text-3)', padding: 0, lineHeight: 1 }}
          >×</button>
        </div>
      )}

      {/* Auto-toast: suggest saving when 2+ filters active */}
      {autoToast && !saveModal && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9998, display: 'flex', alignItems: 'center', gap: 12,
          background: 'var(--c-surface)', border: '1px solid var(--c-border)',
          borderRadius: 12, padding: '12px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,.18)', fontSize: 13,
          animation: 'slideUpFade .25s ease',
        }}>
          <span style={{ fontSize: 18 }}>💾</span>
          <span style={{ color: 'var(--c-text-2)' }}>Want to save this filter combination as a view?</span>
          <button
            className="btn btn--primary btn--sm"
            onClick={() => { setAutoToast(false); setSaveModal(true); }}
          >Save as view</button>
          <button
            onClick={() => { setAutoToast(false); setAutoToastDismissed(true); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--c-text-3)', padding: 0, lineHeight: 1 }}
          >×</button>
        </div>
      )}

      {/* Save view modal */}
      {saveModal && (
        <SaveViewModal
          onSave={handleSaveNewView}
          onCancel={() => setSaveModal(false)}
          saving={savingView}
        />
      )}

      {/* Deduplication panel */}
      {dedupOpen && (
        <DeduplicatePanel
          onClose={() => setDedupOpen(false)}
          getToken={getToken}
          onDone={() => {}}
        />
      )}

      {/* BigDough export modal */}
      {bigDoughModalOpen && (
        <BigDoughExportModal
          onClose={() => setBigDoughModalOpen(false)}
          onExport={handleExportForBigDough}
        />
      )}

      {/* Breakdown count grid over the current filtered view */}
      {breakdownOpen && (
        <BreakdownGrid
          onClose={() => setBreakdownOpen(false)}
          fetchRows={fetchAllContactRows}
          filterSummary={describeActiveFilters()}
        />
      )}

      {/* Batch splitter over the current filtered view */}
      {batchSplitOpen && (
        <BatchSplitPanel
          onClose={() => setBatchSplitOpen(false)}
          fetchRows={fetchAllContactRows}
          filterSummary={describeActiveFilters()}
        />
      )}

      {emailSuggestContact && (() => {
        const sug = getEmailSuggestion(emailSuggestContact);
        if (!sug) return null;
        return (
          <EmailSuggestModal
            contact={emailSuggestContact}
            suggestion={sug}
            onSave={(email) => handleEmailSuggestSave(emailSuggestContact, email)}
            onDismiss={() => {
              setDismissedEmailSuggests((prev) => new Set(prev).add(emailSuggestContact.contact_id));
              setEmailSuggestContact(null);
            }}
            onClose={() => setEmailSuggestContact(null)}
          />
        );
      })()}

      {moveFirmContact && (
        <MoveFirmModal
          contact={moveFirmContact}
          firms={data?.firms ?? []}
          getToken={getToken}
          onClose={() => setMoveFirmContact(null)}
          onMoved={(r) => {
            // Optimistic: swap the firm on the visible row immediately.
            if (r?.firm_id) {
              setContacts((prev) => prev.map((row) => row.contact_id === moveFirmContact.contact_id
                ? { ...row, firm_id: r.firm_id, _firm_name: r.firm_name ?? row._firm_name }
                : row));
            }
            setMoveFirmContact(null);
            setRefreshKey((k) => k + 1); // reload this page's server-side rows
            onRefresh?.();               // refresh the shared bundle (trials/today)
          }}
        />
      )}
    </div>
  );
}
